import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { FileText, AlertTriangle, Zap, ClipboardList, Cpu, Camera, Trash2, PenSquare, Plus, Building2, User, X, Menu, RefreshCcw, Download, Settings, LogOut, FileEdit, ClipboardCheck, ShieldAlert, CheckCircle2, Send, Pencil, Trash, Droplets, Flame, ShieldBan, Info, Sparkles, ChevronDown, ChevronUp, FileSignature, HelpCircle, Antenna, Shield, Waves, MinusCircle } from 'lucide-react';
import ClientPicker from '../../../components/ClientPicker';
import PdfPreviewModal from '../../../components/PdfPreviewModal';
import api, { certificatesAPI, clientsAPI, tenantUsersAPI, testEquipmentAPI } from '../../../api';
import { useAuthStore } from '../../../store/authStore';
import { useToast } from '../../../hooks/useToast';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../../components/ui/Accordion';
import BottomSheet from '../../../components/ui/BottomSheet';
import scheduleTemplate from '../../../data/inspectionScheduleTemplate.json';
import CircuitsManager from './CircuitsManager';
import EicDeclarationTab from './EicDeclarationTab';
import {
  buildCompanyFromSettings,
  createEmptyCompanyInfo,
  DEFAULT_SECTION_DECLARATIONS,
  normalizeRiskAssessmentStatus,
} from './eicDeclarationUtils';
import {
  BS_EN_STANDARDS,
  VOLTAGE_RATINGS,
  RATED_CURRENT_OPTIONS,
  RCD_RATINGS,
  SPD_TYPES,
  NUM_PHASES,
  CONFIRM_OPTIONS,
  TEST_RESULT_OPTIONS,
  EQUIPMENT_OPTIONS,
} from '../../../lib/electricalStandards';
import ComboboxInput from '../../../components/ui/ComboboxInput';
import TextInputWithSuggestions from '../../../components/ui/TextInputWithSuggestions';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import {
  DEVICE_STANDARD_OPTIONS,
  SPD_TYPE_OPTIONS,
  SHORT_CIRCUIT_CAPACITY_KA_OPTIONS,
  SPD_RATED_CURRENT_A_OPTIONS,
  MAIN_SWITCH_POLES_OPTIONS,
  VOLTAGE_RATING_OPTIONS,
  CONDUCTOR_CSA_OPTIONS,
  RCD_OPERATING_CURRENT_OPTIONS,
  EARTH_CONDUCTOR_CSA_OPTIONS,
} from '../../../constants/standards';

const TABS = [
  { key: 'client_installation', label: 'Client & Installation', Icon: FileText },
  { key: 'declaration', label: 'Declaration', Icon: FileSignature },
  { key: 'supply_characteristics', label: 'Supply Characteristics', Icon: Zap },
  { key: 'inspection_schedule', label: 'Inspection Schedule', Icon: ClipboardList },
  { key: 'boards_circuits', label: 'Boards & Circuits', Icon: Cpu },
  { key: 'appendix_notes', label: 'Appendix & Notes', Icon: FileText },
];

const STATUS_OPTIONS = [
  {
    value: 'draft',
    label: 'Draft / In progress',
    description: 'Working copy visible to internal team members.',
    Icon: FileEdit,
  },
  {
    value: 'ready_for_qs',
    label: 'Ready for QS',
    description: 'Hand over to QS for review and approval.',
    Icon: ClipboardCheck,
  },
  {
    value: 'in_qs',
    label: 'In QS',
    description: 'Currently under QS review.',
    Icon: ShieldAlert,
    requiresPermission: true,
  },
  {
    value: 'complete',
    label: 'Complete',
    description: 'QS has approved and the certificate is complete.',
    Icon: CheckCircle2,
    requiresPermission: true,
  },
  {
    value: 'sent_to_client',
    label: 'Sent to client',
    description: 'Certificate issued to the client portal or via email.',
    Icon: Send,
  },
];

const REASON_OPTIONS = [
  'Periodic inspection',
  'Change of occupancy',
  'Landlord compliance',
];

const LEGACY_REASON_MAP = {
  periodic: 'Periodic inspection',
  change_of_occupancy: 'Change of occupancy',
  landlord: 'Landlord compliance',
};

const PREMISES_OPTIONS = [
  'Domestic',
  'Commercial',
  'Industrial',
  'Agricultural',
  'Educational',
  'Medical',
];

const LEGACY_PREMISES_MAP = {
  dwelling: 'Domestic',
  commercial: 'Commercial',
  industrial: 'Industrial',
};

const PRESET_STORAGE_KEY = 'eicFieldPresets';

const PRESET_FIELD_CONFIG = {
  extentCovered: { label: 'Extent covered', placeholder: 'Describe the extent of the inspection...' },
  agreedLimitations: { label: 'Agreed limitations', placeholder: 'Describe any agreed limitations...' },
  operationalLimitations: { label: 'Operational limitations', placeholder: 'Describe any operational limitations...' },
  generalCondition: { label: 'General condition of the installation', placeholder: 'Describe the general condition...' },
  bs7671Departures: { label: 'Details of departures from BS 7671, as amended', placeholder: 'Enter details of any departures from BS 7671...' },
  permittedExceptions: { label: 'Details of permitted exceptions', placeholder: 'Enter details of any permitted exceptions...' },
};

const DEFAULT_FIELD_PRESETS = Object.keys(PRESET_FIELD_CONFIG).reduce((acc, key) => {
  acc[key] = [];
  return acc;
}, {});

const createEmptySignatory = () => ({
  id: '',
  label: '',
  type: '',
  value: '',
});

const parseSignatory = (input, fallbackTenantId = '') => {
  if (!input) return createEmptySignatory();

  if (typeof input === 'string') {
    const label = input.trim();
    const safeLabel = label || '';
    return {
      id: '',
      label: safeLabel,
      type: 'legacy',
      value: safeLabel ? `legacy:${safeLabel}` : '',
    };
  }

  if (typeof input === 'object') {
    const label = (input.label || input.name || '').trim();
    const id = input.id || input.userId || '';
    const rawValue = input.value || '';
    const derivedType = input.type
      || (rawValue.startsWith('tenant:') ? 'tenant'
        : rawValue.startsWith('user:') ? 'user'
          : id ? 'user'
            : label ? 'legacy'
              : '');

    let value = rawValue;
    if (!value) {
      if (derivedType === 'tenant') {
        const tenantRef = id || fallbackTenantId || 'current';
        value = `tenant:${tenantRef}`;
      } else if (derivedType === 'user' && id) {
        value = `user:${id}`;
      } else if (label) {
        value = `legacy:${label}`;
      }
    }

    return {
      id: id || '',
      label,
      type: derivedType,
      value,
    };
  }

  return createEmptySignatory();
};

const serializeSignatory = (signatory) => {
  if (!signatory) return null;
  const label = (signatory.label || '').trim();
  const id = signatory.id ? String(signatory.id) : '';
  const value = signatory.value || (signatory.type === 'tenant'
    ? `tenant:${id || ''}`
    : signatory.type === 'user' && id
      ? `user:${id}`
      : label
        ? `legacy:${label}`
        : '');
  const type = signatory.type || (value.startsWith('tenant:') ? 'tenant' : value.startsWith('user:') ? 'user' : label ? 'legacy' : '');

  if (!label && !id && !value) return null;

  return {
    id: id || null,
    label,
    type,
    value,
  };
};

const serializeSectionDeclarations = (sections) => {
  if (!sections || typeof sections !== 'object') return sections;
  const result = {};
  Object.entries(sections).forEach(([key, section]) => {
    if (!section) return;
    result[key] = {
      ...section,
      engineer: serializeSignatory(section.engineer),
      secondPair: section.secondPair
        ? {
            ...section.secondPair,
            engineer: serializeSignatory(section.secondPair.engineer),
          }
        : null,
    };
  });
  return result;
};

const parseSectionDeclarations = (sections, fallbackTenantId = '') => {
  const base = { ...DEFAULT_SECTION_DECLARATIONS };
  if (!sections || typeof sections !== 'object') return base;
  Object.keys(base).forEach((key) => {
    const section = sections[key];
    if (!section) return;
    base[key] = {
      ...base[key],
      ...section,
      engineer: parseSignatory(section.engineer, fallbackTenantId),
      secondPair: section.secondPair
        ? {
            ...section.secondPair,
            engineer: parseSignatory(section.secondPair.engineer, fallbackTenantId),
          }
        : null,
    };
  });
  return base;
};

const mergeOptionsWithLegacy = (options, signatory) => {
  const base = Array.isArray(options) ? [...options] : [];
  if (!signatory || !signatory.value) {
    return base;
  }

  const alreadyPresent = base.some((opt) => opt.value === signatory.value);
  if (alreadyPresent) {
    return base;
  }

  const baseLabel = signatory.label || signatory.value.replace(/^legacy:/, '') || 'Legacy selection';
  base.push({
    value: signatory.value,
    label: baseLabel.endsWith(' (legacy)') ? baseLabel : `${baseLabel} (legacy)`,
    type: signatory.type || 'legacy',
    disabled: true,
  });
  return base;
};

const createPresetId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `preset-${Math.random().toString(36).slice(2, 11)}-${Date.now().toString(36)}`;
};

const normalizeReasonValue = (value) => {
  if (!value) return '';
  return LEGACY_REASON_MAP[value] || value;
};

const normalizePremisesValue = (value) => {
  if (!value) return '';
  return LEGACY_PREMISES_MAP[value] || value;
};

const DEFAULT_INSTALLATION_SCOPE = {
  newInstallation: false,
  addition: false,
  alteration: false,
  distributionBoardReplacement: false,
};

const normalizeInstallationScope = (value) => {
  if (!value || typeof value !== 'object') return { ...DEFAULT_INSTALLATION_SCOPE };
  return {
    newInstallation: !!value.newInstallation,
    addition: !!value.addition,
    alteration: !!value.alteration,
    distributionBoardReplacement: !!value.distributionBoardReplacement,
  };
};

const EXTRANEOUS_ICON_MAP = {
  water: Droplets,
  gas: Flame,
  oil: Droplets,
  steel: ShieldBan,
  lightning: Zap,
  other: Info,
};

const EXTRANEOUS_BONDING_STATUS = ['PASS', 'FAIL', 'LIM', 'N/A'];

const EXTRANEOUS_BONDING_ITEMS = [
  { label: 'Water', key: 'water' },
  { label: 'GAS', key: 'gas' },
  { label: 'OIL', key: 'oil' },
  { label: 'Structural Steel', key: 'steel' },
  { label: 'Lightning', key: 'lightning' },
  { label: 'Other', key: 'other', editableLabel: true },
];

const normalizeExtraneousBonding = (extraneousBonding = {}) => {
  const bonding = { water: '', gas: '', oil: '', steel: '', lightning: '', other: '', otherLabel: '', ...extraneousBonding };
  const { other, otherLabel, ...rest } = bonding;
  if (other && !EXTRANEOUS_BONDING_STATUS.includes(other)) {
    return {
      ...rest,
      other: '',
      otherLabel: otherLabel || other,
    };
  }
  return { ...rest, other: other || '', otherLabel: otherLabel || '' };
};

const EMPTY_OVERCURRENT_DEVICE = {
  enabled: false,
  deviceType: '',
  deviceRatingSetting: '',
  breakingCapacity: '',
  settingsEnabled: false,
  protectiveDeviceSettings: '',
};

const EMPTY_RCD_DETAILS = {
  enabled: false,
  type: '',
  ratedResidualCurrent: '',
  breakingCapacity: '',
  ratedTimeDelay: '',
  measuredOperatingTime: '',
};

const RCD_TYPE_OPTIONS = ['A', 'AC', 'B', 'F', 'K', 'Z', 'HP', 'EV', 'AKV'];

const OVERCURRENT_DEVICE_RATING_OPTIONS = [
  '5', '6', '10', '15', '16', '20', '25', '32', '40', '50', '63', '80', '100', 'N/A',
];

const hasOvercurrentDeviceData = (oc = {}) =>
  !!(oc.deviceType || oc.deviceRatingSetting || oc.breakingCapacity || oc.protectiveDeviceSettings);

const hasRcdDetailsData = (rcd = {}) =>
  !!(
    rcd.type ||
    rcd.ratedResidualCurrent ||
    rcd.breakingCapacity ||
    rcd.ratedTimeDelay ||
    rcd.measuredOperatingTime ||
    rcd.operatingCurrent ||
    rcd.timeDelay ||
    rcd.operatingTime
  );

const normalizeSupplyParticulars = (particulars = {}) => {
  const oc = particulars.overcurrentDevice || {};
  const rcd = particulars.rcd || {};
  const extraneousBonding = normalizeExtraneousBonding(particulars.extraneousBonding);
  return {
    ...particulars,
    extraneousBonding,
    overcurrentDevice: {
      ...EMPTY_OVERCURRENT_DEVICE,
      deviceType: oc.deviceType || '',
      deviceRatingSetting: oc.deviceRatingSetting || '',
      breakingCapacity: oc.breakingCapacity || '',
      protectiveDeviceSettings: oc.protectiveDeviceSettings || '',
      settingsEnabled: oc.settingsEnabled ?? !!oc.protectiveDeviceSettings,
      enabled: oc.enabled ?? hasOvercurrentDeviceData(oc),
    },
    rcd: {
      ...EMPTY_RCD_DETAILS,
      type: rcd.type || '',
      ratedResidualCurrent: rcd.ratedResidualCurrent || rcd.operatingCurrent || '',
      breakingCapacity: rcd.breakingCapacity || '',
      ratedTimeDelay: rcd.ratedTimeDelay || rcd.timeDelay || '',
      measuredOperatingTime: rcd.measuredOperatingTime || rcd.operatingTime || '',
      enabled: rcd.enabled ?? hasRcdDetailsData(rcd),
    },
  };
};

export default function NewEic() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditMode = !!editId;
  const { success, error: showError, ToastContainer } = useToast();
  const tenantId = useAuthStore((state) => state.user?.tenantId);
  const userRole = useAuthStore((state) => state.user?.role);
  const currentUser = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState('client_installation');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef(null);
  const [certificateStatus, setCertificateStatus] = useState('draft');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const canManageRestrictedStatuses = useMemo(() => {
    if (userRole === 'global_admin') return true;
    return Boolean(currentUser?.isAdmin || currentUser?.isQs);
  }, [currentUser, userRole]);
  const currentStatusOption = useMemo(
    () => STATUS_OPTIONS.find((option) => option.value === certificateStatus) || STATUS_OPTIONS[0],
    [certificateStatus]
  );

  const handleOpenStatusModal = () => {
    setIsHeaderMenuOpen(false);
    setStatusError('');
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    if (statusUpdating) return;
    setIsStatusModalOpen(false);
    setStatusError('');
  };

  const handleStatusSelect = async (newStatus) => {
    if (!newStatus) return;
    setStatusError('');

    if (newStatus === certificateStatus) {
      setIsStatusModalOpen(false);
      return;
    }

    const selectedOption = STATUS_OPTIONS.find((option) => option.value === newStatus);
    if (selectedOption?.requiresPermission && !canManageRestrictedStatuses) {
      setStatusError('Only a QS or Admin can set the status to "In QS" or "Complete".');
      return;
    }

    try {
      setStatusUpdating(true);
      if (serverId) {
        await certificatesAPI.update(serverId, { status: newStatus });
      }
      setCertificateStatus(newStatus);
      saveDraft(undefined, newStatus);
      setIsStatusModalOpen(false);
    } catch (err) {
      setStatusError(err?.message || 'Failed to update certificate status.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const [signatoryOptions, setSignatoryOptions] = useState([]);
  const [signatoriesLoading, setSignatoriesLoading] = useState(false);

  // Basic form state for first tab
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientId, setClientId] = useState('');
  const [uid, setUid] = useState('');
  const [uidLocked, setUidLocked] = useState(false);
  const [serverId, setServerId] = useState(editId || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [dateCreated, setDateCreated] = useState(() => new Date().toISOString().slice(0, 10));
  const [installationAddress, setInstallationAddress] = useState('');
  const [useClientAddress, setUseClientAddress] = useState(false);
  const [reason, setReason] = useState('');
  const [premises, setPremises] = useState('');
  const [recordsAvailable, setRecordsAvailable] = useState(null);
const [installationScope, setInstallationScope] = useState(() => ({ ...DEFAULT_INSTALLATION_SCOPE }));

  // Extent & limitations
  const [extentCovered, setExtentCovered] = useState('');
  const [agreedLimitations, setAgreedLimitations] = useState('');
  const [operationalLimitations, setOperationalLimitations] = useState('');
  const [agreedWith, setAgreedWith] = useState('');

  // Summary & declaration
  const [generalCondition, setGeneralCondition] = useState('');
  const [fieldPresets, setFieldPresets] = useState(() => ({ ...DEFAULT_FIELD_PRESETS }));
  const [presetDrawerOpen, setPresetDrawerOpen] = useState(false);
  const [activePresetField, setActivePresetField] = useState(null);
  const [presetForm, setPresetForm] = useState({ name: '', content: '' });
  const [presetError, setPresetError] = useState('');
  const [inspectedBy, setInspectedBy] = useState(() => createEmptySignatory());
  const [inspectedDate, setInspectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [authorisedBy, setAuthorisedBy] = useState(() => createEmptySignatory());
  const [authorisedDate, setAuthorisedDate] = useState('');

  // Declaration details
  const [declarationMode, setDeclarationMode] = useState('single');
  const [bs7671Departures, setBs7671Departures] = useState('');
  const [permittedExceptions, setPermittedExceptions] = useState('');
  const [riskAssessmentAttached, setRiskAssessmentAttached] = useState('');
  const [singleCompany, setSingleCompany] = useState(() => createEmptyCompanyInfo());
  const [sectionDeclarations, setSectionDeclarations] = useState(() => ({ ...DEFAULT_SECTION_DECLARATIONS }));
  const [tenantCompanyInfo, setTenantCompanyInfo] = useState(() => createEmptyCompanyInfo());
  const presetApplyRef = useRef(null);

  // Recommendations
  const [nextInspectionDate, setNextInspectionDate] = useState('');

  // Overall assessment
  const [overallAssessment, setOverallAssessment] = useState(null); // 'satisfactory' | 'unsatisfactory'

  // Limitation modal
  const [showLimitationModal, setShowLimitationModal] = useState(false);
  const [limitationRefItem, setLimitationRefItem] = useState('');
  const [limitationText, setLimitationText] = useState('');

  // Appendix & Notes
  const [appendixNotes, setAppendixNotes] = useState('');
  const [appendixPhotos, setAppendixPhotos] = useState([]);
  const [appendixFiles, setAppendixFiles] = useState([]);

  // Boards & Circuits
  const [boards, setBoards] = useState([]);
  const [editingBoard, setEditingBoard] = useState(null);
  const [showCircuitsManager, setShowCircuitsManager] = useState(false);
  const [showDeleteBoardModal, setShowDeleteBoardModal] = useState(false);
  
  // Certificate-level circuits (max 3)
  const [certificateCircuits, setCertificateCircuits] = useState([]);
  const [showCertificateCircuitsManager, setShowCertificateCircuitsManager] = useState(false);
  const [boardDraft, setBoardDraft] = useState({
    name: '',
    manufacturer: '',
    location: '',
    photo: null,
    suppliedFrom: '',
    numPhases: '',
    polarityConfirmed: '',
    phaseSequenceConfirmed: '',
    zsAtDb: '',
    zsAtDbNa: false,
    zsAtDbLim: false,
    ipfAtDb: '',
    ipfAtDbNa: false,
    ipfAtDbLim: false,
    mainSwitch: {
      bsEn: '',
      voltageRating: '',
      ratedCurrent: '',
      ipfRating: '',
      ipfRatingNa: false,
      ipfRatingLim: false,
      rcdRating: '',
      rcdTripTime: '',
      rcdTripTimeNa: false,
      rcdTripTimeLim: false,
    },
    spd: {
      type: '',
      functionalCheck: '',
    },
    overcurrentDevice: {
      bsEn: '',
      voltageRating: '',
      ratedCurrent: '',
    },
    testing: {
      engineer: '',
      dateTested: new Date().toISOString().slice(0, 10),
      equipment: {
        multifunctionMft: '',
        continuity: '',
        insulationResistance: '',
        earthLoop: '',
        rcd: '',
        earthElectrode: '',
      },
    },
    notes: '',
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setIsHeaderMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsHeaderMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(PRESET_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setFieldPresets({
          ...DEFAULT_FIELD_PRESETS,
          ...parsed,
        });
      }
    } catch (error) {
      console.error('Failed to load EIC presets', error);
    }
  }, []);

  const getFieldValueByKey = useCallback((fieldKey) => {
    switch (fieldKey) {
      case 'extentCovered':
        return extentCovered || '';
      case 'agreedLimitations':
        return agreedLimitations || '';
      case 'operationalLimitations':
        return operationalLimitations || '';
      case 'generalCondition':
        return generalCondition || '';
      case 'bs7671Departures':
        return bs7671Departures || '';
      case 'permittedExceptions':
        return permittedExceptions || '';
      default:
        return '';
    }
  }, [extentCovered, agreedLimitations, operationalLimitations, generalCondition, bs7671Departures, permittedExceptions]);

  const updatePresetsForField = useCallback((fieldKey, updater) => {
    setFieldPresets((prev) => {
      const currentList = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
      const nextList = typeof updater === 'function' ? updater(currentList) : updater;
      const nextState = {
        ...DEFAULT_FIELD_PRESETS,
        ...prev,
        [fieldKey]: nextList,
      };
      try {
        localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(nextState));
      } catch (error) {
        console.error('Failed to persist EIC presets', error);
      }
      return nextState;
    });
  }, []);

  const handleOpenPresetDrawer = useCallback((fieldKey) => {
    if (!PRESET_FIELD_CONFIG[fieldKey]) return;
    presetApplyRef.current = null;
    setActivePresetField(fieldKey);
    setPresetForm({
      name: '',
      content: getFieldValueByKey(fieldKey),
    });
    setPresetError('');
    setPresetDrawerOpen(true);
  }, [getFieldValueByKey]);

  const handleOpenPresetForTarget = useCallback((fieldKey, applyFn) => {
    if (!PRESET_FIELD_CONFIG[fieldKey] || typeof applyFn !== 'function') return;
    presetApplyRef.current = applyFn;
    setActivePresetField(fieldKey);
    setPresetForm({ name: '', content: '' });
    setPresetError('');
    setPresetDrawerOpen(true);
  }, []);

  const handlePresetDrawerChange = useCallback((open) => {
    setPresetDrawerOpen(open);
    if (!open) {
      presetApplyRef.current = null;
      setActivePresetField(null);
      setPresetForm({ name: '', content: '' });
      setPresetError('');
    }
  }, []);

  const setFieldValueByKey = useCallback((fieldKey, value) => {
    const nextValue = value ?? '';
    switch (fieldKey) {
      case 'extentCovered':
        setExtentCovered(nextValue);
        break;
      case 'agreedLimitations':
        setAgreedLimitations(nextValue);
        break;
      case 'operationalLimitations':
        setOperationalLimitations(nextValue);
        break;
      case 'generalCondition':
        setGeneralCondition(nextValue);
        break;
      case 'bs7671Departures':
        setBs7671Departures(nextValue);
        break;
      case 'permittedExceptions':
        setPermittedExceptions(nextValue);
        break;
      default:
        break;
    }
  }, [setExtentCovered, setAgreedLimitations, setOperationalLimitations, setGeneralCondition, setBs7671Departures, setPermittedExceptions]);

  const handleSavePreset = useCallback(() => {
    if (!activePresetField) return;
    const name = presetForm.name.trim();
    const content = presetForm.content.trim();
    if (!name || !content) {
      setPresetError('Please add both a preset name and some text.');
      return;
    }

    updatePresetsForField(activePresetField, (list) => {
      const filtered = list.filter((item) => item.name.toLowerCase() !== name.toLowerCase());
      return [
        {
          id: createPresetId(),
          name,
          content,
          createdAt: new Date().toISOString(),
        },
        ...filtered,
      ];
    });

    setPresetForm({ name: '', content });
    setPresetError('');
  }, [activePresetField, presetForm, updatePresetsForField]);

  const handleDeletePreset = useCallback((fieldKey, presetId) => {
    updatePresetsForField(fieldKey, (list) => list.filter((item) => item.id !== presetId));
  }, [updatePresetsForField]);

  const handleApplyPreset = useCallback((fieldKey, presetValue) => {
    if (!fieldKey) return;
    if (presetApplyRef.current) {
      presetApplyRef.current(presetValue ?? '');
      presetApplyRef.current = null;
    } else {
      setFieldValueByKey(fieldKey, presetValue ?? '');
    }
    setPresetError('');
    handlePresetDrawerChange(false);
  }, [handlePresetDrawerChange, setFieldValueByKey]);

  // Supply characteristics & earthing arrangements
  const [supply, setSupply] = useState({
    earthingArrangement: '', // TN-S, TN-C-S (PME), TN-C-S (PMB), TN-C, TT, IT
    systemType: 'AC', // AC or DC
    nominalFrequencyHz: '50',
    phase: '1p2w', // 1-phase (2 wire) etc.
    numSupplies: '',
    supplySourceTab: 'origin', // origin
    supplyName: 'Origin',
    otherSourcesOfSupply: '',
    polarityConfirmed: 'YES', // YES/NO/LIM/N/A
    ze: '',
    pfcPscc: '',
    maximumDemand: '',
    spd: { bsEn: '', type: '', shortCircuitCapacity: '', ratedCurrent: '' },
    particulars: {
      meansOfEarthing: '', // distributors_facility | earth_electrode
      earthElectrode: { type: '', resistance: '', location: '' },
      mainSwitch: {
        typeBsEn: '', poles: '', voltageRating: '', ratedCurrentIn: '', fuseSettingA: '',
        location: '', conductorMaterial: '', conductorCsa: ''
      },
      overcurrentDevice: { ...EMPTY_OVERCURRENT_DEVICE },
      rcd: { ...EMPTY_RCD_DETAILS },
      earthConductor: { material: '', csa: '', continuity: '' },
      bondingConductors: { material: '', csa: '', continuity: '' },
      extraneousBonding: { water: '', gas: '', oil: '', steel: '', lightning: '', other: '', otherLabel: '' },
    },
  });

  // Maximum demand help slide-over
  const [maxDemandSlideOverOpen, setMaxDemandSlideOverOpen] = useState(false);

  const [renameSupplyModalOpen, setRenameSupplyModalOpen] = useState(false);
  const [renameSupplyDraft, setRenameSupplyDraft] = useState('Origin');

  const handleOpenRenameSupplyModal = useCallback(() => {
    setRenameSupplyDraft(supply.supplyName || 'Origin');
    setRenameSupplyModalOpen(true);
  }, [supply.supplyName]);

  const handleCloseRenameSupplyModal = useCallback(() => {
    setRenameSupplyModalOpen(false);
  }, []);

  const handleSaveRenameSupply = useCallback(() => {
    const trimmed = renameSupplyDraft.trim();
    setSupply((prev) => ({ ...prev, supplyName: trimmed || 'Origin' }));
    setRenameSupplyModalOpen(false);
  }, [renameSupplyDraft]);

  const [otherSourcesModalOpen, setOtherSourcesModalOpen] = useState(false);
  const [otherSourcesDraft, setOtherSourcesDraft] = useState('');

  const hasOtherSourcesData = Boolean(supply.otherSourcesOfSupply?.trim());

  const handleOpenOtherSourcesModal = useCallback(() => {
    setOtherSourcesDraft(supply.otherSourcesOfSupply || '');
    setOtherSourcesModalOpen(true);
  }, [supply.otherSourcesOfSupply]);

  const handleCloseOtherSourcesModal = useCallback(() => {
    setOtherSourcesModalOpen(false);
  }, []);

  const handleSaveOtherSources = useCallback(() => {
    setSupply((prev) => ({ ...prev, otherSourcesOfSupply: otherSourcesDraft }));
    setOtherSourcesModalOpen(false);
  }, [otherSourcesDraft]);

  const [extraneousOtherLabelModalOpen, setExtraneousOtherLabelModalOpen] = useState(false);
  const [extraneousOtherLabelDraft, setExtraneousOtherLabelDraft] = useState('');

  const extraneousOtherDisplayLabel = supply.particulars.extraneousBonding.otherLabel?.trim() || 'Other';

  const handleOpenExtraneousOtherLabelModal = useCallback(() => {
    setExtraneousOtherLabelDraft(supply.particulars.extraneousBonding.otherLabel || '');
    setExtraneousOtherLabelModalOpen(true);
  }, [supply.particulars.extraneousBonding.otherLabel]);

  const handleCloseExtraneousOtherLabelModal = useCallback(() => {
    setExtraneousOtherLabelModalOpen(false);
  }, []);

  const handleSaveExtraneousOtherLabel = useCallback(() => {
    setSupply((prev) => ({
      ...prev,
      particulars: {
        ...prev.particulars,
        extraneousBonding: {
          ...prev.particulars.extraneousBonding,
          otherLabel: extraneousOtherLabelDraft.trim(),
        },
      },
    }));
    setExtraneousOtherLabelModalOpen(false);
  }, [extraneousOtherLabelDraft]);

  // Inspection schedule (accordion-based)
  const INSPECTION_OPTIONS = [
    { key: 'OK', label: '✓' },
    { key: 'NA', label: 'N/A' },
  ];
  const [inspection, setInspection] = useState({
    sections: scheduleTemplate.sections,
    status: '',
  });

  // Accordion expand/collapse state
  const [expandedSections, setExpandedSections] = useState([]);

  // Engineer equipment cache - stores equipment for each engineer
  const [engineerEquipmentCache, setEngineerEquipmentCache] = useState({});
  const engineerEquipmentCacheRef = useRef({});

  // Map database equipment_type to form field names
  const EQUIPMENT_TYPE_MAP = {
    'Multifunction': 'multifunctionMft',
    'Continuity': 'continuity',
    'Insulation Resistance': 'insulationResistance',
    'Earth loop': 'earthLoop',
    'RCD': 'rcd',
    // Note: 'Earth Electrode' doesn't exist in DB, will need to handle separately
  };

  // Fetch equipment for an engineer
  const fetchEngineerEquipment = useCallback(async (userId) => {
    if (!userId || !tenantId) return {};
    
    // Check cache first using ref
    if (engineerEquipmentCacheRef.current[userId]) {
      return engineerEquipmentCacheRef.current[userId];
    }

    try {
      const response = await testEquipmentAPI.getByUser(userId, tenantId);
      const equipmentList = response.equipment || [];
      
      console.log('Fetched equipment for user:', userId, equipmentList);
      
      // Group equipment by category
      const equipmentByCategory = {
        multifunctionMft: [],
        continuity: [],
        insulationResistance: [],
        earthLoop: [],
        rcd: [],
        earthElectrode: [], // Will remain empty unless we add support
      };

      equipmentList.forEach((eq) => {
        const category = EQUIPMENT_TYPE_MAP[eq.equipmentType];
        console.log('Processing equipment:', { 
          equipmentType: eq.equipmentType, 
          category, 
          model: eq.model, 
          serialNumber: eq.serialNumber 
        });
        
        if (category) {
          // Try to match model or serialNumber against EQUIPMENT_OPTIONS
          // Check multiple matching strategies:
          // 1. Exact match on model
          // 2. Exact match on serialNumber
          // 3. Case-insensitive contains match
          const model = eq.model?.trim() || '';
          const serialNumber = eq.serialNumber?.trim() || '';
          
          let matchedEquipment = null;
          
          // Try exact match first
          matchedEquipment = EQUIPMENT_OPTIONS.find(opt => 
            opt.value === model || 
            opt.label === model ||
            opt.value === serialNumber ||
            opt.label === serialNumber
          );
          
          // If no exact match, try case-insensitive contains match
          if (!matchedEquipment && model) {
            matchedEquipment = EQUIPMENT_OPTIONS.find(opt => 
              model.toLowerCase().includes(opt.value.toLowerCase()) ||
              model.toLowerCase().includes(opt.label.toLowerCase())
            );
          }
          
          if (!matchedEquipment && serialNumber) {
            matchedEquipment = EQUIPMENT_OPTIONS.find(opt => 
              serialNumber.toLowerCase().includes(opt.value.toLowerCase()) ||
              serialNumber.toLowerCase().includes(opt.label.toLowerCase())
            );
          }
          
          if (matchedEquipment) {
            equipmentByCategory[category].push(matchedEquipment.value);
            console.log('Matched equipment:', matchedEquipment.value, 'for category:', category);
          } else {
            // If no match found, use model or serialNumber as fallback
            // This ensures equipment shows up even if it doesn't match EQUIPMENT_OPTIONS exactly
            const fallbackValue = model || serialNumber;
            if (fallbackValue) {
              equipmentByCategory[category].push(fallbackValue);
              console.log('Added fallback equipment:', fallbackValue, 'for category:', category);
            }
            // Debug: log unmatched equipment
            console.log('Unmatched equipment (using fallback):', {
              equipmentType: eq.equipmentType,
              model: eq.model,
              serialNumber: eq.serialNumber,
              manufacturer: eq.manufacturer,
              fallbackValue,
              category
            });
          }
        } else {
          console.log('No category mapping found for equipment_type:', eq.equipmentType);
        }
      });

      // Update both ref and state cache
      engineerEquipmentCacheRef.current[userId] = equipmentByCategory;
      setEngineerEquipmentCache(prev => ({
        ...prev,
        [userId]: equipmentByCategory
      }));

      console.log('Processed equipment by category:', equipmentByCategory);
      return equipmentByCategory;
    } catch (error) {
      console.error('Failed to fetch engineer equipment', error);
      return {};
    }
  }, [tenantId]);

  // Get equipment options for a specific engineer and category
  const getEngineerEquipment = useCallback((engineerValue, category) => {
    if (!engineerValue) return EQUIPMENT_OPTIONS;
    
    // Extract user ID from engineer value (format: "user:uuid" or just uuid)
    const userId = engineerValue.startsWith('user:') 
      ? engineerValue.replace('user:', '') 
      : engineerValue;
    
    const refCache = engineerEquipmentCacheRef.current[userId];
    const stateCache = engineerEquipmentCache[userId];
    const engineerEquipment = refCache || stateCache;
    
    console.log('getEngineerEquipment called:', {
      engineerValue,
      userId,
      category,
      hasRefCache: !!refCache,
      hasStateCache: !!stateCache,
      engineerEquipment,
      categoryEquipment: engineerEquipment?.[category]
    });
    
    if (!engineerEquipment || !engineerEquipment[category] || engineerEquipment[category].length === 0) {
      console.log('No equipment found for category:', category);
      return []; // Return empty array if no equipment - will show N/A
    }
    
    // Get equipment values for this category
    const equipmentValues = engineerEquipment[category];
    console.log('Equipment values for category:', category, equipmentValues);
    
    // Match against EQUIPMENT_OPTIONS first, then add any unmatched values
    const matchedOptions = EQUIPMENT_OPTIONS.filter(eq => equipmentValues.includes(eq.value));
    const unmatchedValues = equipmentValues.filter(val => !EQUIPMENT_OPTIONS.some(opt => opt.value === val));
    
    console.log('Matched options:', matchedOptions, 'Unmatched values:', unmatchedValues);
    
    // Combine matched options with unmatched values (as simple objects)
    const allOptions = [
      ...matchedOptions,
      ...unmatchedValues.map(val => ({ value: val, label: val }))
    ];
    
    console.log('Returning all options:', allOptions);
    return allOptions;
  }, [engineerEquipmentCache]);

  // Draft autosave management
  const formatDateForUid = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}${mm}${dd}`;
  };

  const generateUid = () => {
    const datePart = formatDateForUid(new Date());
    const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
    return `EIC-${datePart}-${rand}`;
  };

  // Load clients on mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await clientsAPI.getAll({ limit: 100 });
        setClients(response.clients || []);
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSignatories = async () => {
      if (!tenantId) {
        if (isMounted) {
          setSignatoryOptions([]);
          setSignatoriesLoading(false);
        }
        return;
      }

      setSignatoriesLoading(true);
      try {
        const [usersResult, settingsResult] = await Promise.allSettled([
          tenantUsersAPI.list(tenantId),
          api.get('/settings', userRole === 'global_admin' && tenantId ? { params: { tenantId } } : undefined),
        ]);

        if (!isMounted) return;

        const nextOptions = [];

        if (usersResult.status === 'fulfilled') {
          const users = Array.isArray(usersResult.value?.users) ? usersResult.value.users : [];
          users
            .filter((user) => user.isActive !== false)
            .forEach((user) => {
              const nameParts = [user.firstName, user.lastName].filter(Boolean);
              const label = nameParts.length > 0 ? nameParts.join(' ') : (user.email || 'Unnamed user');
              nextOptions.push({
                value: `user:${user.id}`,
                label,
                type: 'user',
                meta: {
                  id: user.id,
                  email: user.email,
                  role: user.role,
                },
              });
            });
          nextOptions.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        } else {
          console.error('Failed to load tenant users', usersResult.reason);
        }

        let tenantLabel = '';
        if (settingsResult.status === 'fulfilled') {
          const settings = settingsResult.value?.data?.settings || {};
          tenantLabel = settings.company_name || '';
          const companyInfo = buildCompanyFromSettings(settings);
          setTenantCompanyInfo(companyInfo);
          setSingleCompany((prev) => (prev?.useCustom ? prev : { ...companyInfo, useCustom: false }));
          setSectionDeclarations((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((key) => {
              if (!next[key].company?.useCustom) {
                next[key] = { ...next[key], company: { ...companyInfo, useCustom: false } };
              }
              if (next[key].secondPair && !next[key].secondPair.company?.useCustom) {
                next[key] = {
                  ...next[key],
                  secondPair: {
                    ...next[key].secondPair,
                    company: { ...companyInfo, useCustom: false },
                  },
                };
              }
            });
            return next;
          });
        } else if (settingsResult.status === 'rejected') {
          console.error('Failed to load tenant settings', settingsResult.reason);
        }

        if (!nextOptions.some((opt) => opt.type === 'tenant')) {
          nextOptions.push({
            value: `tenant:${tenantId}`,
            label: tenantLabel || 'Tenant Account',
            type: 'tenant',
            meta: {
              id: tenantId,
            },
          });
        }

        setSignatoryOptions(nextOptions);
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load signatories', error);
          setSignatoryOptions([]);
        }
      } finally {
        if (isMounted) {
          setSignatoriesLoading(false);
        }
      }
    };

    loadSignatories();

    return () => {
      isMounted = false;
    };
  }, [tenantId, userRole]);

  const inspectedByOptions = useMemo(
    () => mergeOptionsWithLegacy(signatoryOptions, inspectedBy),
    [signatoryOptions, inspectedBy]
  );

  const authorisedByOptions = useMemo(
    () => mergeOptionsWithLegacy(signatoryOptions, authorisedBy),
    [signatoryOptions, authorisedBy]
  );

  const handleSignatorySelectChange = useCallback((value, setter, availableOptions) => {
    if (!value) {
      setter(createEmptySignatory());
      return;
    }

    const match = availableOptions.find((opt) => opt.value === value);

    if (match) {
      const optionId = match.meta?.id
        || (match.type === 'tenant' ? tenantId || ''
          : value.startsWith('user:') ? value.split(':')[1] : '');
      setter({
        id: optionId || '',
        label: match.label,
        type: match.type,
        value: match.value,
      });
      return;
    }

    const label = value.startsWith('legacy:') ? value.slice('legacy:'.length) : value;
    setter({
      id: '',
      label,
      type: 'legacy',
      value,
    });
  }, [tenantId]);

  useEffect(() => {
    const syncFromOptions = (signatory, setter) => {
      if (!signatory || !signatory.value) return;
      const match = signatoryOptions.find((opt) => opt.value === signatory.value);
      if (!match) return;

      const optionId = match.meta?.id
        || (match.type === 'tenant' ? tenantId || '' : signatory.id || '');

      if (match.label === signatory.label && (optionId || '') === (signatory.id || '')) {
        return;
      }

      setter({
        id: optionId || '',
        label: match.label,
        type: match.type,
        value: match.value,
      });
    };

    syncFromOptions(inspectedBy, setInspectedBy);
    syncFromOptions(authorisedBy, setAuthorisedBy);
  }, [signatoryOptions, inspectedBy, authorisedBy, tenantId]);

  // Load or create draft on mount
  useEffect(() => {
    if (isEditMode) {
      // Load existing certificate for editing
      (async () => {
        try {
          setLoading(true);
          const cert = await certificatesAPI.getOne(editId);
          // Basic top-level mappings
          setUid(cert.uid || cert.certificateNum || '');
          setClientId(cert.client?.id || cert.clientId || '');
          setSelectedClient(cert.client || null);
          // Ensure date is in yyyy-MM-dd format for date inputs
          const dateCreatedValue = cert.dateCreated 
            ? (cert.dateCreated.includes('T') ? cert.dateCreated.split('T')[0] : cert.dateCreated)
            : new Date().toISOString().slice(0, 10);
          setDateCreated(dateCreatedValue);
          setInstallationAddress(cert.installationAddress || '');
          setPremises(normalizePremisesValue(cert.installationType));
          // Data payload (form sections)
          const d = cert.data || {};
          setReason(normalizeReasonValue(d.reason));
          setRecordsAvailable(d.recordsAvailable ?? null);
          setInstallationScope(normalizeInstallationScope(d.installationScope));
          setUseClientAddress(!!d.useClientAddress);
          setExtentCovered(d.extentCovered || '');
          setAgreedLimitations(d.agreedLimitations || '');
          setOperationalLimitations(d.operationalLimitations || '');
          setAgreedWith(d.agreedWith || '');
          setGeneralCondition(d.generalCondition || '');
          setInspectedBy(parseSignatory(d.inspectedBy, tenantId || ''));
          // Ensure dates are in yyyy-MM-dd format for date inputs
          const inspectedDateValue = d.inspectedDate 
            ? (d.inspectedDate.includes('T') ? d.inspectedDate.split('T')[0] : d.inspectedDate)
            : new Date().toISOString().slice(0, 10);
          setInspectedDate(inspectedDateValue);
          setAuthorisedBy(parseSignatory(d.authorisedBy, tenantId || ''));
          const authorisedDateValue = d.authorisedDate 
            ? (d.authorisedDate.includes('T') ? d.authorisedDate.split('T')[0] : d.authorisedDate)
            : '';
          setAuthorisedDate(authorisedDateValue);
          setDeclarationMode(d.declarationMode === 'multiple' ? 'multiple' : 'single');
          setBs7671Departures(d.bs7671Departures || '');
          setPermittedExceptions(d.permittedExceptions || '');
          setRiskAssessmentAttached(normalizeRiskAssessmentStatus(d.riskAssessmentAttached));
          setSingleCompany(d.singleCompany || createEmptyCompanyInfo());
          setSectionDeclarations(parseSectionDeclarations(d.sectionDeclarations, tenantId || ''));
          const nextInspectionDateValue = d.nextInspectionDate 
            ? (d.nextInspectionDate.includes('T') ? d.nextInspectionDate.split('T')[0] : d.nextInspectionDate)
            : '';
          setNextInspectionDate(nextInspectionDateValue);
          setOverallAssessment(d.overallAssessment || null);
          setBoards(Array.isArray(d.boards) ? d.boards : []);
          setCertificateCircuits(Array.isArray(d.certificateCircuits) ? d.certificateCircuits : []);
          setAppendixNotes(d.appendixNotes || '');
          setAppendixPhotos(Array.isArray(d.appendixPhotos) ? d.appendixPhotos : []);
          setAppendixFiles(Array.isArray(d.appendixFiles) ? d.appendixFiles : []);
          if (d.supply) {
            // Ensure earthElectrode is initialized if missing
            const supplyData = {
              ...d.supply,
              particulars: normalizeSupplyParticulars({
                ...d.supply.particulars,
                earthElectrode: d.supply.particulars?.earthElectrode || { type: '', resistance: '', location: '' },
              }),
            };
            setSupply(supplyData);
          }
          if (d.inspection) setInspection(d.inspection);
          setServerId(cert.id);
          setUidLocked(true);
          setCertificateStatus(cert.status || 'draft');
        } catch (err) {
          console.error('Failed to load certificate', err);
        } finally {
          setLoading(false);
        }
      })();
      return; // skip local draft bootstrap in edit mode
    }
    const currentUid = localStorage.getItem('eicCurrentDraftUid');
    if (currentUid) {
      const raw = localStorage.getItem(`eicDraft:${currentUid}`);
      if (raw) {
        try {
          const draft = JSON.parse(raw);
          setUid(draft.uid || currentUid);
          setClientId(draft.clientId || '');
          setSelectedClient(draft.selectedClient || null);
          setReason(normalizeReasonValue(draft.reason));
          setDateCreated(draft.dateCreated || new Date().toISOString().slice(0, 10));
          setInstallationAddress(draft.installationAddress || '');
          setUseClientAddress(!!draft.useClientAddress);
          setPremises(normalizePremisesValue(draft.premises));
          setRecordsAvailable(draft.recordsAvailable ?? null);
          setInstallationScope(normalizeInstallationScope(draft.installationScope));
          setExtentCovered(draft.extentCovered || '');
          setAgreedLimitations(draft.agreedLimitations || '');
          setOperationalLimitations(draft.operationalLimitations || '');
          setAgreedWith(draft.agreedWith || '');
          setGeneralCondition(draft.generalCondition || '');
          setInspectedBy(parseSignatory(draft.inspectedBy, tenantId || ''));
          setInspectedDate(draft.inspectedDate || new Date().toISOString().slice(0, 10));
          setAuthorisedBy(parseSignatory(draft.authorisedBy, tenantId || ''));
          setAuthorisedDate(draft.authorisedDate || '');
          setDeclarationMode(draft.declarationMode === 'multiple' ? 'multiple' : 'single');
          setBs7671Departures(draft.bs7671Departures || '');
          setPermittedExceptions(draft.permittedExceptions || '');
          setRiskAssessmentAttached(normalizeRiskAssessmentStatus(draft.riskAssessmentAttached));
          setSingleCompany(draft.singleCompany || createEmptyCompanyInfo());
          setSectionDeclarations(parseSectionDeclarations(draft.sectionDeclarations, tenantId || ''));
          setNextInspectionDate(draft.nextInspectionDate || '');
          setOverallAssessment(draft.overallAssessment || null);
          setBoards(Array.isArray(draft.boards) ? draft.boards : []);
          setCertificateCircuits(Array.isArray(draft.certificateCircuits) ? draft.certificateCircuits : []);
          setAppendixNotes(draft.appendixNotes || '');
          setAppendixPhotos(Array.isArray(draft.appendixPhotos) ? draft.appendixPhotos : []);
          setAppendixFiles(Array.isArray(draft.appendixFiles) ? draft.appendixFiles : []);
          setCertificateStatus(draft.status || 'draft');
          return;
        } catch (_) {
          // fall through to create fresh draft
        }
      }
    }
    const newUid = generateUid();
    setUid(newUid);
    localStorage.setItem('eicCurrentDraftUid', newUid);
    // initial persist
    setTimeout(() => saveDraft(newUid), 0);
  }, []);

  // Helper to remove base64 data from images (keeps metadata only)
  const sanitizeImage = (img) => {
    if (!img) return img;
    // Keep metadata but remove dataUrl to reduce payload size
    const { dataUrl, ...rest } = img;
    return rest;
  };

  // Helper to sanitize array of images
  const sanitizeImages = (images) => {
    if (!Array.isArray(images)) return images;
    return images.map(sanitizeImage);
  };

  // Helper to sanitize boards (remove base64 photos)
  const sanitizeBoard = (board) => {
    if (!board) return board;
    return {
      ...board,
      photo: sanitizeImage(board.photo),
      circuits: Array.isArray(board.circuits) 
        ? board.circuits.map(circuit => ({
            ...circuit,
            photo: sanitizeImage(circuit.photo),
          }))
        : [],
    };
  };

  // Build draft - sanitize images for server, keep full data for localStorage
  const buildDraft = (uidValue = uid, forServer = false, statusValue = certificateStatus) => {
    const draft = {
      uid: uidValue,
      clientId,
      selectedClient: forServer ? null : selectedClient, // Don't need full client object on server
      status: statusValue,
      reason,
      dateCreated,
      installationAddress,
      useClientAddress,
      premises,
      recordsAvailable,
      installationScope,
      extentCovered,
      agreedLimitations,
      operationalLimitations,
      agreedWith,
      generalCondition,
      inspectedBy: serializeSignatory(inspectedBy),
      inspectedDate,
      authorisedBy: serializeSignatory(authorisedBy),
      authorisedDate,
      declarationMode,
      bs7671Departures,
      permittedExceptions,
      riskAssessmentAttached,
      singleCompany,
      sectionDeclarations: serializeSectionDeclarations(sectionDeclarations),
      nextInspectionDate,
      overallAssessment,
      supply,
      inspection,
      appendixNotes,
      certificateCircuits, // Certificate-level circuits (max 3)
      lastSavedAt: new Date().toISOString(),
    };

    if (forServer) {
      // Strip base64 image data for server (reduces payload by 80-95%)
      draft.boards = Array.isArray(boards) ? boards.map(sanitizeBoard) : [];
      draft.appendixPhotos = sanitizeImages(appendixPhotos);
      draft.appendixFiles = sanitizeImages(appendixFiles);
    } else {
      // Keep full data for localStorage
      draft.boards = boards;
      draft.appendixPhotos = appendixPhotos;
      draft.appendixFiles = appendixFiles;
    }

    return draft;
  };

  const saveDraft = (uidValue = uid, statusValue = certificateStatus) => {
    if (!uidValue) return;
    const data = buildDraft(uidValue, false, statusValue);
    localStorage.setItem(`eicDraft:${uidValue}`, JSON.stringify(data));
    localStorage.setItem('eicCurrentDraftUid', uidValue);
  };

  // Server-side draft persist (create/update)
  const persistServerDraft = async () => {
    if (!clientId) return; // require client before server save
    const payload = {
      certificateType: 'eic',
      certificateNum: uid || certificateNumber, // Use uid as certificate number
      clientId,
      dateCreated,
      installationAddress,
      installationType: premises,
      status: certificateStatus,
      data: buildDraft(uid, true, certificateStatus), // forServer=true removes base64 data
    };
    try {
      setSaving(true);
      if (serverId) {
        await certificatesAPI.update(serverId, payload);
      } else {
        const created = await certificatesAPI.create(payload);
        setServerId(created.id);
        setUidLocked(true);
      }
    } catch (err) {
      console.error('Failed to persist server draft', err);
    } finally {
      setSaving(false);
    }
  };

  // Complete save handler - saves all EIC data to database
  const handleSaveEic = async () => {
    // Validation
    if (!clientId || !clientId.trim()) {
      setSaveError('Please select a client before saving.');
      return;
    }
    
    // Basic UUID validation (simple check)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(clientId.trim())) {
      setSaveError('Invalid client selected. Please select a client again.');
      return;
    }
    
    const certNum = uid || certificateNumber;
    if (!certNum || !certNum.trim()) {
      setSaveError('Please enter a certificate number (UID) before saving.');
      return;
    }

    setSaveError('');
    setSaving(true);

    try {
      // Build complete payload with all EIC data
      // Use forServer=true to strip base64 images and reduce payload size
      const payload = {
        certificateType: 'eic',
        certificateNum: certNum.trim(),
        clientId: clientId.trim(),
        dateCreated: dateCreated || new Date().toISOString().split('T')[0],
        dateIssued: inspectedDate || null,
        dateCompleted: inspectedDate || null,
        installationAddress: installationAddress || null,
        installationType: premises || null,
        jobReference: reason || null,
        jobDescription: reason || null,
        status: certificateStatus,
        notes: appendixNotes || null,
        // All form data goes into the JSONB data field
        // Images are stripped (base64 removed) but metadata preserved
        data: buildDraft(uid, true, certificateStatus), // forServer=true removes base64 data
      };

      // Log payload size for debugging
      const payloadStr = JSON.stringify(payload);
      const payloadSizeKB = (payloadStr.length / 1024).toFixed(2);
      console.log(`Payload size: ${payloadSizeKB} KB`);
      
      if (payloadStr.length > 1024 * 100) { // Warn if over 100KB
        console.warn('Large payload detected. Images have been stripped but payload is still large.');
      }

      console.log('Saving EIC with payload:', { ...payload, data: '[...]' }); // Log without huge data field

      if (serverId) {
        // Update existing certificate
        const result = await certificatesAPI.update(serverId, payload);
        console.log('Update result:', result);
      } else {
        // Create new certificate
        const created = await certificatesAPI.create(payload);
        console.log('Create result:', created);
        setServerId(created.id);
        if (!uid) {
          setUid(certNum.trim());
        }
        setUidLocked(true);
      }

      // Success - show notification
      success('Changes saved', 'Your EIC has been saved successfully.');
    } catch (err) {
      console.error('Failed to save EIC - Full error:', err);
      console.error('Error type:', typeof err);
      console.error('Error response:', err.response?.data);
      console.error('Error response errors:', err.response?.data?.errors);
      console.error('Error message:', err.message);
      
      // Better error message extraction
      // Note: axios interceptor may wrap errors, but we check both structures
      let errorMessage = 'Failed to save EIC. Please try again.';
      
      // Check if error has response property (original axios error)
      if (err.response?.data) {
        if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.errors && Array.isArray(err.response.data.errors) && err.response.data.errors.length > 0) {
          errorMessage = err.response.data.errors[0].msg || err.response.data.errors[0] || errorMessage;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message && err.message !== 'An error occurred') {
        // Use error message if it's not the generic one from interceptor
        errorMessage = err.message;
      }
      
      setSaveError(errorMessage);
      showError('Failed to save', errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Handle PDF export
  const handleExportPdf = async () => {
    setIsHeaderMenuOpen(false);
    setPdfError('');
    setPdfLoading(true);

    try {
      // Validate required fields
      if (!clientId || !clientId.trim()) {
        setPdfError('Please select a client before exporting.');
        setPdfLoading(false);
        return;
      }

      const certNum = uid || certificateNumber;
      if (!certNum || !certNum.trim()) {
        setPdfError('Please enter a certificate number (UID) before exporting.');
        setPdfLoading(false);
        return;
      }

      // Ensure certificate is saved to server first
      let certificateId = serverId;
      if (!certificateId) {
        // Save certificate first
        const payload = {
          certificateType: 'eic',
          certificateNum: certNum.trim(),
          clientId: clientId.trim(),
          dateCreated: dateCreated || new Date().toISOString().split('T')[0],
          dateIssued: inspectedDate || null,
          dateCompleted: inspectedDate || null,
          installationAddress: installationAddress || null,
          installationType: premises || null,
          jobReference: reason || null,
          jobDescription: reason || null,
          status: certificateStatus,
          notes: appendixNotes || null,
          data: buildDraft(uid, true, certificateStatus),
        };

        const result = await certificatesAPI.create(payload);
        certificateId = result.id;
        setServerId(certificateId);
      } else {
        // Update existing certificate to ensure latest data
        const payload = {
          dateIssued: inspectedDate || null,
          dateCompleted: inspectedDate || null,
          installationAddress: installationAddress || null,
          installationType: premises || null,
          jobReference: reason || null,
          jobDescription: reason || null,
          status: certificateStatus,
          notes: appendixNotes || null,
          data: buildDraft(uid, true, certificateStatus),
        };
        await certificatesAPI.update(certificateId, payload);
      }

      // Generate a temporary signed link for the PDF (more reliable than blob URLs)
      // This creates a public URL that browsers can open directly
      console.log('Generating temporary PDF link for certificate:', certificateId);
      
      const linkResponse = await certificatesAPI.generatePdfLink(certificateId);
      const { publicUrl, token, expiresAt } = linkResponse;
      
      console.log('PDF link generated:', {
        publicUrl,
        token: token.substring(0, 20) + '...',
        expiresAt
      });
      
      // Set the public URL for opening/downloading
      setPdfUrl(publicUrl);
      
      // Show modal - user can open in new tab or download
      setShowPdfModal(true);
    } catch (err) {
      console.error('Failed to export PDF - Full error:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.status);
      console.error('Error message:', err.message);
      
      let errorMessage = 'Failed to generate PDF. Please try again.';
      
      // Try to extract error message from various sources
      if (err.message && err.message !== 'An error occurred') {
        errorMessage = err.message;
      } else if (err.response?.data) {
        // Handle blob error responses
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (parseErr) {
            errorMessage = `Server error (${err.response.status || err.status || 'unknown'})`;
          }
        } else if (typeof err.response.data === 'object') {
          errorMessage = err.response.data.error || err.response.data.message || errorMessage;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      } else if (err.status) {
        errorMessage = `Server error (${err.status})`;
      }
      
      setPdfError(errorMessage);
      // Show error in a toast or alert
      alert(`PDF Export Failed: ${errorMessage}`);
    } finally {
      setPdfLoading(false);
    }
  };

  // Close PDF modal
  const handleClosePdfModal = () => {
    setPdfUrl(null);
    setShowPdfModal(false);
    setPdfError('');
  };

  // Debounced autosave on key fields
  useEffect(() => {
    const id = setTimeout(() => saveDraft(), 500);
    return () => clearTimeout(id);
  }, [
    uid,
    clientId,
    reason,
    dateCreated,
    installationAddress,
    useClientAddress,
    premises,
    recordsAvailable,
    extentCovered,
    agreedLimitations,
    operationalLimitations,
    agreedWith,
    generalCondition,
    inspectedBy,
    inspectedDate,
    authorisedBy,
    authorisedDate,
    declarationMode,
    bs7671Departures,
    permittedExceptions,
    riskAssessmentAttached,
    JSON.stringify(singleCompany),
    JSON.stringify(sectionDeclarations),
    nextInspectionDate,
    overallAssessment,
    JSON.stringify(supply),
    JSON.stringify(inspection),
    JSON.stringify(boards),
    appendixNotes,
    appendixPhotos,
    appendixFiles,
  ]);

  // Debounce server save when clientId present
  useEffect(() => {
    if (!clientId) return;
    const id = setTimeout(() => { persistServerDraft(); }, 1000);
    return () => clearTimeout(id);
  }, [
    clientId,
    uid,
    reason,
    dateCreated,
    installationAddress,
    useClientAddress,
    premises,
    recordsAvailable,
    extentCovered,
    agreedLimitations,
    operationalLimitations,
    agreedWith,
    generalCondition,
    inspectedBy,
    inspectedDate,
    authorisedBy,
    authorisedDate,
    declarationMode,
    bs7671Departures,
    permittedExceptions,
    riskAssessmentAttached,
    JSON.stringify(singleCompany),
    JSON.stringify(sectionDeclarations),
    nextInspectionDate,
    overallAssessment,
    JSON.stringify(supply),
    JSON.stringify(inspection),
    JSON.stringify(boards),
    appendixNotes,
    appendixPhotos,
    appendixFiles,
  ]);

  const handleRefreshTenantCompanyInfo = useCallback(async () => {
    try {
      const response = await api.get('/settings', userRole === 'global_admin' && tenantId ? { params: { tenantId } } : undefined);
      const settings = response?.data?.settings || {};
      const companyInfo = buildCompanyFromSettings(settings);
      setTenantCompanyInfo(companyInfo);
      setSingleCompany((prev) => (prev?.useCustom ? prev : { ...companyInfo, useCustom: false }));
    } catch (error) {
      console.error('Failed to refresh tenant company info', error);
    }
  }, [tenantId, userRole]);

  const handleChangeTab = (key) => {
    saveDraft();
    setActiveTab(key);
  };

  const getClientDisplayName = (client) => {
    if (!client) return '';
    if (client.client_type === 'company') {
      return client.company_name || 'Unnamed Company';
    }
    const name = `${client.first_name || ''} ${client.last_name || ''}`.trim();
    return name || 'Unnamed Individual';
  };

  const clientDisplayName = useMemo(() => {
    return getClientDisplayName(selectedClient);
  }, [selectedClient]);

  const clientAddressString = useMemo(() => {
    if (!selectedClient) return '';
    const parts = [
      selectedClient.address_line1,
      selectedClient.address_line2,
      selectedClient.city,
      selectedClient.county,
      selectedClient.postcode,
      selectedClient.country,
    ].filter(Boolean);
    return parts.join(', ');
  }, [selectedClient]);


  const handleAppendixFiles = async (files) => {
    const fileArr = Array.from(files || []);
    const reads = await Promise.all(
      fileArr.map((file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              id:
                crypto && crypto.randomUUID
                  ? crypto.randomUUID()
                  : Math.random().toString(36).slice(2),
              name: file.name,
              type: file.type,
              size: file.size,
              dataUrl: reader.result,
              title: '',
              caption: '',
            });
          reader.readAsDataURL(file);
        })
      )
    );
    setAppendixPhotos((prev) => [...prev, ...reads]);
  };

  const moveAppendixPhoto = (id, direction) => {
    setAppendixPhotos((prev) => {
      const index = prev.findIndex((p) => p.id === id);
      if (index < 0) return prev;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      next.splice(target, 0, removed);
      return next;
    });
  };

  // Handle file uploads (PDFs, Microsoft docs, images)
  const handleAppendixFileUpload = async (files) => {
    const fileArr = Array.from(files || []);
    
    // Validate file types
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    
    const validFiles = fileArr.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(file.type) || allowedExtensions.includes(ext);
    });

    if (validFiles.length !== fileArr.length) {
      showError('Invalid file type', 'Some files were not uploaded. Only PDFs, Microsoft Office documents, and images are allowed.');
    }

    if (validFiles.length === 0) return;

    const reads = await Promise.all(
      validFiles.map((file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            resolve({
              id:
                crypto && crypto.randomUUID
                  ? crypto.randomUUID()
                  : Math.random().toString(36).slice(2),
              name: file.name,
              type: file.type,
              size: file.size,
              extension: ext,
              dataUrl: reader.result,
              description: '',
            });
          };
          reader.readAsDataURL(file);
        })
      )
    );
    setAppendixFiles((prev) => [...prev, ...reads]);
  };

  const moveAppendixFile = (id, direction) => {
    setAppendixFiles((prev) => {
      const index = prev.findIndex((f) => f.id === id);
      if (index < 0) return prev;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      next.splice(target, 0, removed);
      return next;
    });
  };

  // Helper to get file icon based on extension
  const getFileIcon = (extension) => {
    const ext = extension.toLowerCase();
    if (ext === '.pdf') return '📄';
    if (['.doc', '.docx'].includes(ext)) return '📝';
    if (['.xls', '.xlsx'].includes(ext)) return '📊';
    if (['.ppt', '.pptx'].includes(ext)) return '📽️';
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) return '🖼️';
    return '📎';
  };

  // Helper to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const activePresetLabel = activePresetField ? PRESET_FIELD_CONFIG[activePresetField]?.label : '';
  const presetsForActiveField = activePresetField ? fieldPresets[activePresetField] || [] : [];

  const openLimitationFromInspection = (itemCode) => {
    setLimitationRefItem(itemCode);
    setLimitationText('');
    setShowLimitationModal(true);
  };

  const getInspectionActiveClasses = (key) => {
    switch (key) {
      case 'OK':
        return 'bg-green-600 border-green-600 text-white';
      case 'C1':
        return 'bg-red-700 border-red-700 text-white';
      case 'C2':
        return 'bg-red-500 border-red-500 text-white';
      case 'C3':
        return 'bg-amber-500 border-amber-500 text-white';
      case 'FI':
        return 'bg-yellow-400 border-yellow-400 text-gray-900';
      case 'FAIL':
        return 'bg-red-700 border-red-700 text-white';
      // NV, LIM, NA stay as they are (blue accent)
      case 'NV':
      case 'LIM':
      case 'NA':
        return 'border-blue-500 text-blue-600 bg-white dark:bg-gray-700 dark:text-blue-400';
      default:
        return 'border-blue-500 text-blue-600 bg-white dark:bg-gray-700 dark:text-blue-400';
    }
  };

  const handleUseClientAddressToggle = (checked) => {
    setUseClientAddress(checked);
    if (checked) {
      setInstallationAddress(clientAddressString);
    }
  };

const handleInstallationScopeToggle = (key) => {
  setInstallationScope((prev) => ({
    newInstallation: key === 'newInstallation' ? !prev.newInstallation : false,
    addition: key === 'addition' ? !prev.addition : false,
    alteration: key === 'alteration' ? !prev.alteration : false,
    distributionBoardReplacement: key === 'distributionBoardReplacement' ? !prev.distributionBoardReplacement : false,
  }));
};

  const handleAddBoard = () => {
    setBoardDraft({
      name: '',
      manufacturer: '',
      location: '',
      photo: null,
      suppliedFrom: '',
      numPhases: '',
      polarityConfirmed: '',
      phaseSequenceConfirmed: '',
      zsAtDb: '',
      zsAtDbNa: false,
      zsAtDbLim: false,
      ipfAtDb: '',
      ipfAtDbNa: false,
      ipfAtDbLim: false,
      mainSwitch: {
        bsEn: '',
        voltageRating: '',
        ratedCurrent: '',
        ipfRating: '',
        ipfRatingNa: false,
        ipfRatingLim: false,
        rcdRating: '',
        rcdTripTime: '',
        rcdTripTimeNa: false,
        rcdTripTimeLim: false,
      },
      spd: {
        type: '',
        functionalCheck: '',
      },
      overcurrentDevice: {
        bsEn: '',
        voltageRating: '',
        ratedCurrent: '',
      },
      testing: {
        engineer: '',
        dateTested: new Date().toISOString().slice(0, 10),
        equipment: {
          multifunctionMft: '',
          continuity: '',
          insulationResistance: '',
          earthLoop: '',
          rcd: '',
          earthElectrode: '',
        },
      },
      notes: '',
    });
    setEditingBoard('new'); // 'new' means new board
  };

  const handleEditBoard = (board) => {
    setBoardDraft({
      name: board.name || '',
      manufacturer: board.manufacturer || '',
      location: board.location || '',
      photo: board.photo || null,
      suppliedFrom: board.suppliedFrom || '',
      numPhases: board.numPhases || '',
      polarityConfirmed: board.polarityConfirmed || '',
      phaseSequenceConfirmed: board.phaseSequenceConfirmed || '',
      zsAtDb: board.zsAtDb || '',
      zsAtDbNa: board.zsAtDbNa || false,
      zsAtDbLim: board.zsAtDbLim || false,
      ipfAtDb: board.ipfAtDb || '',
      ipfAtDbNa: board.ipfAtDbNa || false,
      ipfAtDbLim: board.ipfAtDbLim || false,
      mainSwitch: board.mainSwitch || {
        bsEn: '',
        voltageRating: '',
        ratedCurrent: '',
        ipfRating: '',
        ipfRatingNa: false,
        ipfRatingLim: false,
        rcdRating: '',
        rcdTripTime: '',
        rcdTripTimeNa: false,
        rcdTripTimeLim: false,
      },
      spd: board.spd || { type: '', functionalCheck: '' },
      overcurrentDevice: board.overcurrentDevice || { bsEn: '', voltageRating: '', ratedCurrent: '' },
      testing: board.testing || {
        engineer: '',
        dateTested: new Date().toISOString().slice(0, 10),
        equipment: {
          multifunctionMft: '',
          continuity: '',
          insulationResistance: '',
          earthLoop: '',
          rcd: '',
          earthElectrode: '',
        },
      },
      notes: board.notes || '',
    });
    setEditingBoard(board.id);
  };

  const handleSaveBoard = () => {
    if (!boardDraft.name.trim()) {
      alert('Board name is required');
      return;
    }
    const boardData = {
      ...boardDraft,
      circuits: editingBoard && editingBoard !== 'new' ? boards.find(b => b.id === editingBoard)?.circuits || [] : [],
    };
    if (editingBoard && editingBoard !== 'new') {
      setBoards((prev) => prev.map((b) => (b.id === editingBoard ? { ...b, ...boardData } : b)));
    } else {
      const newBoard = {
        id: (crypto && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
        ...boardData,
      };
      setBoards((prev) => [...prev, newBoard]);
    }
    setEditingBoard(null); // back to list view
    saveDraft();
    persistServerDraft();
  };

  const handleBoardPhoto = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setBoardDraft((prev) => ({ ...prev, photo: { dataUrl: reader.result, name: file.name } }));
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteBoard = () => {
    if (editingBoard && editingBoard !== 'new') {
      setBoards(prev => prev.filter(b => b.id !== editingBoard));
      setEditingBoard(null);
      saveDraft();
      persistServerDraft();
      setShowDeleteBoardModal(false);
    }
  };

  const handleOpenCircuitsManager = () => {
    if (!boardDraft.name.trim()) {
      alert('Please assign a board name first before managing circuits.');
      return;
    }
    // If editing a new board, save it first
    if (editingBoard === 'new') {
      const boardData = {
        ...boardDraft,
        circuits: [],
      };
      const newBoard = {
        id: crypto.randomUUID(),
        ...boardData,
      };
      setBoards(prev => [...prev, newBoard]);
      setEditingBoard(newBoard.id);
      saveDraft();
      persistServerDraft();
    }
    setShowCircuitsManager(true);
  };

  const handleCloseCircuitsManager = () => {
    setShowCircuitsManager(false);
  };

  // Certificate-level circuits handlers (max 3)
  const handleOpenCertificateCircuitsManager = () => {
    setShowCertificateCircuitsManager(true);
  };

  const handleCloseCertificateCircuitsManager = () => {
    setShowCertificateCircuitsManager(false);
  };

  const handleSaveCertificateCircuits = (circuits) => {
    setCertificateCircuits(circuits);
    saveDraft();
    persistServerDraft();
  };

  const handleSaveCircuits = (circuits) => {
    // Determine which board to update
    let boardId;
    if (editingBoard && editingBoard !== 'new') {
      boardId = editingBoard;
    } else {
      // Find the board by name (most recently added should match)
      boardId = boards.find(b => b.name === boardDraft.name)?.id;
    }
    
    if (boardId) {
      setBoards(prev => prev.map(b => 
        b.id === boardId ? { ...b, circuits } : b
      ));
      saveDraft();
      persistServerDraft();
    }
  };

  // Get current board data for CircuitsManager
  const getCurrentBoardData = () => {
    let currentBoard;
    if (editingBoard && editingBoard !== 'new') {
      currentBoard = boards.find(b => b.id === editingBoard);
    } else {
      currentBoard = boards.find(b => b.name === boardDraft.name);
    }
    return currentBoard || boardDraft;
  };

  // Show CircuitsManager if flag is set
  if (showCircuitsManager) {
    return (
      <CircuitsManager
        boardData={getCurrentBoardData()}
        onSave={handleSaveCircuits}
        onBack={handleCloseCircuitsManager}
      />
    );
  }

  // Show Certificate CircuitsManager if flag is set (max 3 circuits)
  if (showCertificateCircuitsManager) {
    return (
      <CircuitsManager
        boardData={{ circuits: certificateCircuits, name: 'Certificate Circuits' }}
        onSave={handleSaveCertificateCircuits}
        onBack={handleCloseCertificateCircuitsManager}
        maxCircuits={3}
      />
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <button
                onClick={() => navigate('/tenant/certification')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
              >
                ← Back to Certificates
              </button>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">EIC</h1>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${
                      ['in_qs', 'complete'].includes(certificateStatus)
                        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200'
                        : certificateStatus === 'sent_to_client'
                          ? 'border-blue-200 dark:border-blue-800 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200'
                          : certificateStatus === 'ready_for_qs'
                            ? 'border-amber-200 dark:border-amber-800 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200'
                            : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        ['in_qs', 'complete'].includes(certificateStatus)
                          ? 'bg-emerald-500'
                          : certificateStatus === 'sent_to_client'
                            ? 'bg-blue-500'
                            : certificateStatus === 'ready_for_qs'
                              ? 'bg-amber-500'
                              : 'bg-gray-400'
                      }`}
                    />
                    {currentStatusOption.label}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Electrical Installation Certificate</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Requirements for electrical installations (BS 7671-2018+A3:2024 IET Wiring Regulations)</p>
              </div>
                <div className="w-full md:w-auto">
                  <FormLabel htmlFor="certificate-uid">Certificate Unique ID</FormLabel>
                  <Input
                    id="certificate-uid"
                    type="text"
                    value={uid}
                    onChange={(e) => {
                      const newUid = e.target.value;
                      setUid(newUid);
                      // Re-key the draft if UID changes
                      saveDraft(newUid);
                    }}
                    disabled={uidLocked}
                    className="w-full md:w-[360px] h-8"
                    placeholder={`EIC-${formatDateForUid(new Date())}-XXXXXX`}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{uidLocked ? 'UID locked after first save.' : `Editable. Suggested: EIC-${formatDateForUid(new Date())}-XXXXXX`}</p>
                </div>
              </div>
            </div>
            <div ref={headerMenuRef} className="relative self-end md:self-start">
              <button
                type="button"
                onClick={() => setIsHeaderMenuOpen((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={isHeaderMenuOpen}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              >
                <Menu className="h-4 w-4" />
                Menu
              </button>
              {isHeaderMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 z-30">
                  <button
                    type="button"
                    onClick={handleOpenStatusModal}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Change status
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    disabled={pdfLoading}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-4 w-4" />
                    {pdfLoading ? 'Generating PDF...' : 'Export certificate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsHeaderMenuOpen(false)}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-sm text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsHeaderMenuOpen(false)}
                    className="w-full px-4 py-2 flex items-center gap-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Exit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-2 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleChangeTab(key)}
                className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg border transition-colors
                  ${activeTab === key
                    ? 'bg-white dark:bg-gray-800 border-orange-400 dark:border-orange-500 shadow text-gray-900 dark:text-gray-100'
                    : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'}
                `}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm font-medium text-center">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          {activeTab === 'client_installation' && (
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Client & Installation</h2>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Details of client or person ordering report */}
                <div className="bg-[#0B0D10] border border-gray-200 dark:border-gray-700 rounded-xl p-4 h-full">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Details of client or person ordering report</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Client lookup */}
                    <div>
                      <FormLabel htmlFor="client-select">Client</FormLabel>
                      <div className="flex gap-2">
                        <Select
                          id="client-select"
                          value={clientId}
                          onChange={(e) => {
                            const client = clients.find(c => c.id === e.target.value);
                            setSelectedClient(client || null);
                            setClientId(e.target.value);
                            if (useClientAddress && client) {
                              const parts = [client.address_line1, client.address_line2, client.city, client.county, client.postcode, client.country].filter(Boolean);
                              setInstallationAddress(parts.join(', '));
                            }
                            saveDraft();
                          }}
                          className="flex-1 h-8"
                        >
                          <option value="">Select client...</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>
                              {getClientDisplayName(client)}
                            </option>
                          ))}
                        </Select>
                        <button
                          type="button"
                          onClick={() => setShowAddClientModal(true)}
                          className="h-8 px-3 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                          title="Add new client"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      {selectedClient && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate" title={clientAddressString}>{clientAddressString || 'No address on file'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details of the installation */}
                <div className="bg-[#0B0D10] border border-gray-200 dark:border-gray-700 rounded-xl p-4 h-full">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Details of the installation</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <FormLabel htmlFor="installation-address">Installation Address</FormLabel>
                      <Textarea
                        id="installation-address"
                        rows={3}
                        value={installationAddress}
                        onChange={(e) => setInstallationAddress(e.target.value)}
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          id="use-client-address"
                          type="checkbox"
                          checked={useClientAddress}
                          onChange={(e) => handleUseClientAddressToggle(e.target.checked)}
                          className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <label htmlFor="use-client-address" className="text-xs text-gray-700 dark:text-gray-300">Use client address</label>
                      </div>
                      <div className="mt-4">
                        <FormLabel htmlFor="description-of-premises">Description of premises</FormLabel>
                        <TextInputWithSuggestions
                          id="description-of-premises"
                          name="description_of_premises"
                          value={premises}
                          onChange={(e) => setPremises(e.target.value)}
                          options={PREMISES_OPTIONS}
                          placeholder="Select or type description…"
                          showAllOnFocus
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extent and limitations of inspection and testing */}
              <div className="mt-6 bg-[#0B0D10] border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Description and extent of the installation</h3>
                <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <FormLabel htmlFor="extent-covered" spacing={false}>Extent covered</FormLabel>
                      <button
                        type="button"
                        onClick={() => handleOpenPresetDrawer('extentCovered')}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Presets
                      </button>
                    </div>
                    <Textarea id="extent-covered" rows={3} value={extentCovered} onChange={(e) => setExtentCovered(e.target.value)} placeholder="Describe the extent of the inspection..." />
                  </div>
                  <div>
                    <FormLabel className="mb-3">Installation is</FormLabel>
                    <div className="space-y-2">
                      {[
                        { key: 'newInstallation', label: 'New installation' },
                        { key: 'addition', label: 'An addition to an existing installation' },
                        { key: 'alteration', label: 'An alteration to an existing installation' },
                        { key: 'distributionBoardReplacement', label: 'Replacement of a distribution board?' },
                      ].map((option) => (
                        <label
                          key={option.key}
                          htmlFor={`installation-scope-${option.key}`}
                          className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300"
                        >
                          <input
                            id={`installation-scope-${option.key}`}
                            type="checkbox"
                            checked={!!installationScope?.[option.key]}
                            onChange={() => handleInstallationScopeToggle(option.key)}
                            className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <FormLabel htmlFor="general-condition" spacing={false}>Comments on the existing installation</FormLabel>
                      <button
                        type="button"
                        onClick={() => handleOpenPresetDrawer('generalCondition')}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                      >
                        Presets
                      </button>
                    </div>
                    <Textarea id="general-condition" rows={5} value={generalCondition} onChange={(e) => setGeneralCondition(e.target.value)} placeholder="Describe the general condition..." />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">In the case of an addition or alteration see Regulation 644.1.2</p>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="mt-6">
                <div className="bg-[#0B0D10] border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Next inspection</h3>
                  
                  <FormLabel htmlFor="next-inspection-date">I/We recommend that this installation is further inspected and tested after an interval of not more than:</FormLabel>
                  <div className="flex gap-2 mb-3">
                    <Input id="next-inspection-date" type="date" value={nextInspectionDate} onChange={(e) => setNextInspectionDate(e.target.value)} variant="borderless" className="flex-1 h-8" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: '6 Months', months: 6 },
                      { label: '1 Year', months: 12 },
                      { label: '3 Years', months: 36 },
                      { label: '5 Years', months: 60 },
                      { label: '10 Years', months: 120 },
                    ].map((opt) => (
                      <button key={opt.label} type="button" onClick={() => {
                        const d = new Date();
                        d.setMonth(d.getMonth() + opt.months);
                        setNextInspectionDate(d.toISOString().slice(0, 10));
                      }} className="h-8 w-[4.5rem] px-1 text-[10px] leading-tight text-center border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600">{opt.label}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => { saveDraft(); persistServerDraft(); handleChangeTab('declaration'); }}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next: Declaration →
                </button>
              </div>
            </section>
          )}

          {activeTab === 'declaration' && (
            <>
              <EicDeclarationTab
                declarationMode={declarationMode}
                onDeclarationModeChange={setDeclarationMode}
                bs7671Departures={bs7671Departures}
                onBs7671DeparturesChange={setBs7671Departures}
                permittedExceptions={permittedExceptions}
                onPermittedExceptionsChange={setPermittedExceptions}
                riskAssessmentAttached={riskAssessmentAttached}
                onRiskAssessmentAttachedChange={setRiskAssessmentAttached}
                inspectedBy={inspectedBy}
                onInspectedByChange={setInspectedBy}
                inspectedDate={inspectedDate}
                onInspectedDateChange={setInspectedDate}
                authorisedBy={authorisedBy}
                onAuthorisedByChange={setAuthorisedBy}
                authorisedDate={authorisedDate}
                onAuthorisedDateChange={setAuthorisedDate}
                singleCompany={singleCompany}
                onSingleCompanyChange={(patch) => setSingleCompany((prev) => ({ ...prev, ...patch }))}
                sectionDeclarations={sectionDeclarations}
                onSectionDeclarationsChange={setSectionDeclarations}
                tenantCompanyInfo={tenantCompanyInfo}
                onRefreshTenantCompanyInfo={handleRefreshTenantCompanyInfo}
                signatoryOptions={signatoryOptions}
                signatoriesLoading={signatoriesLoading}
                handleSignatorySelectChange={handleSignatorySelectChange}
                inspectedByOptions={inspectedByOptions}
                authorisedByOptions={authorisedByOptions}
                onOpenPresetDrawer={handleOpenPresetDrawer}
                onOpenPresetForTarget={handleOpenPresetForTarget}
              />
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('client_installation')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Client & Installation</button>
                <button type="button" onClick={() => { saveDraft(); persistServerDraft(); handleChangeTab('supply_characteristics'); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Next: Supply Characteristics →</button>
              </div>
            </>
          )}

          {activeTab === 'supply_characteristics' && (
            <section>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-[5px]">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Supply characteristics and earthing arrangements</h2>
                <div className="inline-flex items-stretch gap-4">
                  <button
                    type="button"
                    onClick={handleOpenOtherSourcesModal}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs transition-colors ${
                      hasOtherSourcesData
                        ? 'text-green-500 dark:text-green-400 hover:text-green-600 dark:hover:text-green-300'
                        : 'text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300'
                    }`}
                  >
                    <Antenna className="h-3.5 w-3.5 shrink-0" />
                    Other sources
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenRenameSupplyModal}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5 shrink-0" />
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => setSupply((prev) => ({
                      ...prev,
                      supplySourceTab: 'origin',
                      numSupplies: prev.numSupplies || '1',
                    }))}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs transition-colors font-semibold text-gray-900 dark:text-gray-100"
                  >
                    {supply.supplyName || 'Origin'}
                    <Zap className="h-3.5 w-3.5 shrink-0 text-yellow-400" />
                  </button>
                </div>
              </div>

              {/* Supply characteristics & earthing arrangements */}
              <div className="bg-[#0B0D10] border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-[4.77fr_2.53fr_2fr_2.7fr] gap-4">
                  <div>
                    <FormLabel htmlFor="earthing-arrangement">Earthing arrangement</FormLabel>
                    <div className="inline-flex w-full rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
                      {[
                        { value: 'TN-S', label: 'TN-S' },
                        { value: 'TN-C-S (PME)', label: 'TN-C-S (PME)', legacyValues: ['TN-C-S'] },
                        { value: 'TN-C-S (PMB)', label: 'TN-C-S (PMB)' },
                        { value: 'TN-C', label: 'TN-C' },
                        { value: 'TT', label: 'TT' },
                        { value: 'IT', label: 'IT' },
                      ].map((option, idx, arr) => {
                        const isSelected =
                          supply.earthingArrangement === option.value ||
                          (option.legacyValues?.includes(supply.earthingArrangement) ?? false);
                        const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
                        const classes = isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600';
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={()=>setSupply(prev=>({...prev, earthingArrangement: option.value}))}
                            className={`px-2 py-2 text-[11px] inline-flex items-center justify-center min-w-[58px] border border-gray-300 dark:border-gray-600 ${rounding} ${classes}`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <FormLabel htmlFor="earth-loop-impedance">Earth loop impedance (Ze-Ohms)</FormLabel>
                      <Input id="earth-loop-impedance" type="text" value={supply.ze} onChange={(e)=>setSupply(prev=>({...prev, ze:e.target.value}))} className="w-full h-8" />
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {['N/A','LIM'].map(v => (
                          <button
                            key={v}
                            type="button"
                            onClick={()=>setSupply(prev=>({...prev, ze:v}))}
                            className="inline-flex items-center justify-center px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3">
                      <FormLabel htmlFor="pfc-pscc">PFC/PSCC (kA)</FormLabel>
                      <Input id="pfc-pscc" type="text" value={supply.pfcPscc} onChange={(e)=>setSupply(prev=>({...prev, pfcPscc:e.target.value}))} className="w-full h-8" />
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {['N/A','LIM'].map(v => (
                          <button
                            key={v}
                            type="button"
                            onClick={()=>setSupply(prev=>({...prev, pfcPscc:v}))}
                            className="inline-flex items-center justify-center px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="border-l border-r border-gray-300 dark:border-gray-600 px-4">
                    <FormLabel htmlFor="system">System</FormLabel>
                    <div className="inline-flex w-full rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
                      {['AC','DC'].map((v, idx, arr) => {
                        const isSelected = supply.systemType === v;
                        const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
                        const classes = isSelected
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                          : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600';
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={()=>setSupply(prev=>({...prev, systemType: v}))}
                            className={`px-3 py-2 text-xs inline-flex items-center justify-center w-full border border-gray-300 dark:border-gray-600 ${rounding} ${classes}`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          {key:'1p2w',label:'1-phase (2 wire)'},
                          {key:'1p3w',label:'1-phase (3 wire)'},
                          {key:'2p3w',label:'2-phase (3 wire)'},
                          {key:'3p3w',label:'3-phase (3 wire)'},
                          {key:'3p4w',label:'3-phase (4 wire)'}
                        ].map(o => (
                          <label key={o.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <input type="radio" name="phase" checked={supply.phase===o.key} onChange={() => setSupply(prev => ({...prev, phase:o.key}))} className="text-blue-600" />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-3">
                      <FormLabel htmlFor="nominal-voltage-u" spacing={false}>Nominal voltage U (V)</FormLabel>
                      <FormLabel htmlFor="voltage-uo" spacing={false}>Voltage Uo (V)</FormLabel>
                      <ComboboxInput
                        id="nominal-voltage-u"
                        name="nominal_voltage_u"
                        value={supply.nominalVoltageU || ''}
                        onChange={(e)=>setSupply(prev=>({...prev, nominalVoltageU: e.target.value}))}
                        options={[230, 'N/A', 'LIM']}
                      />
                      <ComboboxInput
                        id="nominal-voltage-uo"
                        name="nominal_voltage_uo"
                        value={supply.nominalVoltageUo || ''}
                        onChange={(e)=>setSupply(prev=>({...prev, nominalVoltageUo: e.target.value}))}
                        options={[230, 400, 'N/A', 'LIM']}
                      />
                    </div>
                    <FormLabel htmlFor="nominal-frequency">Nominal frequency (Hz)</FormLabel>
                    <Input id="nominal-frequency" type="text" value={supply.nominalFrequencyHz} onChange={(e)=>setSupply(prev=>({...prev, nominalFrequencyHz:e.target.value}))} className="w-full h-8" />
                    <div className="mt-3">
                      <FormLabel htmlFor="supply-polarity" spacing={false}>Supply polarity confirmed</FormLabel>
                      <div className="inline-flex w-full rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
                        {['YES','NO','LIM','N/A'].map((v, idx, arr)=> {
                          const isSelected = supply.polarityConfirmed === v;
                          const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
                          const classes = isSelected
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                            : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600';
                          return (
                            <button
                              key={v}
                              type="button"
                              onClick={()=>setSupply(prev=>({...prev, polarityConfirmed:v}))}
                              className={`px-3 py-2 text-xs inline-flex items-center justify-center flex-1 border border-gray-300 dark:border-gray-600 ${rounding} ${classes}`}
                            >
                              {v}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="border-l border-gray-300 dark:border-gray-600 pl-4 flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <FormLabel htmlFor="supply-spd-bsen" spacing={false}>Supply protective device BS(EN)</FormLabel>
                      <ComboboxInput
                        id="supply-spd-bsen"
                        name="supply_spd_bsen"
                        value={supply.spd.bsEn}
                        onChange={(e)=>setSupply(prev=>({...prev, spd:{...prev.spd, bsEn:e.target.value}}))}
                        options={DEVICE_STANDARD_OPTIONS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <FormLabel htmlFor="supply-spd-type" spacing={false}>Type</FormLabel>
                      <ComboboxInput
                        id="supply-spd-type"
                        name="supply_spd_type"
                        value={supply.spd.type}
                        onChange={(e)=>setSupply(prev=>({...prev, spd:{...prev.spd, type:e.target.value}}))}
                        options={SPD_TYPE_OPTIONS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <FormLabel htmlFor="supply-spd-scc-ka" spacing={false}>Short circuit capacity (kA)</FormLabel>
                      <ComboboxInput
                        id="supply-spd-scc-ka"
                        name="supply_spd_scc_ka"
                        value={supply.spd.shortCircuitCapacity}
                        onChange={(e)=>setSupply(prev=>({...prev, spd:{...prev.spd, shortCircuitCapacity:e.target.value}}))}
                        options={SHORT_CIRCUIT_CAPACITY_KA_OPTIONS}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <FormLabel htmlFor="supply-spd-rated-current" spacing={false}>Rated current (A)</FormLabel>
                      <ComboboxInput
                        id="supply-spd-rated-current"
                        name="supply_spd_rated_current"
                        value={supply.spd.ratedCurrent}
                        onChange={(e)=>setSupply(prev=>({...prev, spd:{...prev.spd, ratedCurrent:e.target.value}}))}
                        options={SPD_RATED_CURRENT_A_OPTIONS}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {otherSourcesModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div
                    className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-lg border border-gray-200 dark:border-gray-600 shadow-xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="other-sources-title"
                  >
                    <div className="px-5 py-4 flex items-start justify-between gap-4">
                      <h3 id="other-sources-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Other sources of supply
                      </h3>
                      <button
                        type="button"
                        onClick={handleCloseOtherSourcesModal}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        aria-label="Close other sources dialog"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="px-5 pb-5">
                      <Textarea
                        id="other-sources-of-supply"
                        value={otherSourcesDraft}
                        onChange={(e) => setOtherSourcesDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') handleCloseOtherSourcesModal();
                        }}
                        placeholder="e.g. PV inverter, generator, battery storage system..."
                        className="min-h-[120px] text-sm border-gray-300 dark:border-gray-500"
                        autoFocus
                      />
                      <p className="mt-3 text-sm italic text-gray-500 dark:text-gray-400">
                        Describe any other sources of electrical energy present at or affecting this installation.
                      </p>
                      <div className="mt-5 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleCloseOtherSourcesModal}
                          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveOtherSources}
                          className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {renameSupplyModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div
                    className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-md border border-gray-200 dark:border-gray-600 shadow-xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="rename-supply-title"
                  >
                    <div className="px-5 py-4 flex items-start justify-between gap-4">
                      <h3 id="rename-supply-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Rename supply
                      </h3>
                      <button
                        type="button"
                        onClick={handleCloseRenameSupplyModal}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        aria-label="Close rename supply dialog"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="px-5 pb-5">
                      <Input
                        id="rename-supply-name"
                        type="text"
                        value={renameSupplyDraft}
                        onChange={(e) => setRenameSupplyDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRenameSupply();
                          if (e.key === 'Escape') handleCloseRenameSupplyModal();
                        }}
                        className="w-full h-10 text-sm border-gray-300 dark:border-gray-500"
                        autoFocus
                      />
                      <p className="mt-3 text-sm italic text-gray-500 dark:text-gray-400">
                        Use a descriptive name to easily identify this supply later. This shows up on the list of certificates.
                      </p>
                      <div className="mt-5 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleCloseRenameSupplyModal}
                          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveRenameSupply}
                          className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {extraneousOtherLabelModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div
                    className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-md border border-gray-200 dark:border-gray-600 shadow-xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="extraneous-other-label-title"
                  >
                    <div className="px-5 py-4 flex items-start justify-between gap-4">
                      <h3 id="extraneous-other-label-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Name other bonding item
                      </h3>
                      <button
                        type="button"
                        onClick={handleCloseExtraneousOtherLabelModal}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        aria-label="Close rename bonding item dialog"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="px-5 pb-5">
                      <FormLabel htmlFor="extraneous-other-label-input">Service name</FormLabel>
                      <Input
                        id="extraneous-other-label-input"
                        type="text"
                        value={extraneousOtherLabelDraft}
                        onChange={(e) => setExtraneousOtherLabelDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveExtraneousOtherLabel();
                          if (e.key === 'Escape') handleCloseExtraneousOtherLabelModal();
                        }}
                        placeholder="e.g. LPG, Swimming pool"
                        className="w-full h-10 text-sm border-gray-300 dark:border-gray-500 mt-1"
                        autoFocus
                      />
                      <p className="mt-3 text-sm italic text-gray-500 dark:text-gray-400">
                        Leave blank to use the default label &quot;Other&quot;.
                      </p>
                      <div className="mt-5 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={handleCloseExtraneousOtherLabelModal}
                          className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveExtraneousOtherLabel}
                          className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Particulars of installation referred to in this report */}
              <div className="bg-[#0B0D10] border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Particulars of installation referred to in this report</h3>
                <div className="grid grid-cols-1 md:grid-cols-[0.6fr_1.9fr_0.5fr] gap-4 items-start">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Means of earthing</label>
                    <div className="flex flex-col gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <label className="inline-flex items-center gap-2"><input type="radio" name="moe" checked={supply.particulars.meansOfEarthing==='distributors_facility'} onChange={()=>setSupply(prev=>({...prev, particulars:{...prev.particulars, meansOfEarthing:'distributors_facility'}}))} /> Distributor's facility</label>
                      <label className="inline-flex items-center gap-2"><input type="radio" name="moe" checked={supply.particulars.meansOfEarthing==='earth_electrode'} onChange={()=>setSupply(prev=>({...prev, particulars:{...prev.particulars, meansOfEarthing:'earth_electrode'}}))} /> Earth Electrode</label>
                    </div>
                  </div>
                  <div className="border-l border-r border-gray-300 dark:border-gray-600 px-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Earth conductor</h4>
                    <div className="grid grid-cols-3 gap-2 items-end">
                      <div className="flex flex-col gap-1 min-w-0">
                        <FormLabel htmlFor="earth-conductor-material" spacing={false}>Material</FormLabel>
                        <Select id="earth-conductor-material" value={supply.particulars.earthConductor.material} onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, earthConductor:{...prev.particulars.earthConductor, material:e.target.value}}}))} className="w-full h-8 text-xs">
                          <option value="">Select…</option>
                          <option value="copper">Copper</option>
                          <option value="aluminium">Aluminium</option>
                          <option value="steel">Steel</option>
                          <option value="other">Other</option>
                          <option value="n/a">N/A</option>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-1 min-w-0">
                        <FormLabel htmlFor="earth-conductor-csa" spacing={false}>C.S.A (mm2)</FormLabel>
                        <ComboboxInput
                          id="earth-conductor-csa"
                          name="earth_conductor_csa"
                          value={supply.particulars.earthConductor.csa}
                          onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, earthConductor:{...prev.particulars.earthConductor, csa:e.target.value}}}))}
                          options={EARTH_CONDUCTOR_CSA_OPTIONS}
                          className="!text-xs"
                        />
                      </div>
                      <div className="flex flex-col gap-1 min-w-0">
                        <FormLabel htmlFor="earth-conductor-continuity" spacing={false}>Continuity check</FormLabel>
                        <div className="inline-flex w-full rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
                          {['PASS', 'FAIL', 'N/A', 'LIM'].map((v, idx, arr) => {
                            const isSelected = supply.particulars.earthConductor.continuity === v;
                            const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
                            const classes = isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                              : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600';
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setSupply(prev => ({
                                  ...prev,
                                  particulars: {
                                    ...prev.particulars,
                                    earthConductor: { ...prev.particulars.earthConductor, continuity: v },
                                  },
                                }))}
                                className={`px-1 py-2 text-[10px] inline-flex items-center justify-center flex-1 border border-gray-300 dark:border-gray-600 ${rounding} ${classes}`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <span className="flex items-center gap-2">
                        Maximum demand
                        <button
                          type="button"
                          onClick={() => setMaxDemandSlideOverOpen(true)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                          title="Help"
                        >
                          <HelpCircle className="w-4 h-4" />
                        </button>
                      </span>
                    </label>
                    <input
                      type="text"
                      value={supply.maximumDemand}
                      onChange={(e) => setSupply(prev => ({ ...prev, maximumDemand: e.target.value }))}
                      className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter maximum demand..."
                    />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {['N/A', 'LIM'].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setSupply((prev) => ({ ...prev, maximumDemand: v }))}
                          className="inline-flex items-center justify-center px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Earth Electrode Details Card - shown when Earth Electrode is selected */}
                {supply.particulars.meansOfEarthing === 'earth_electrode' && (
                  <div className="mt-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Details of installation earth electrode</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Type */}
                      <div>
                        <FormLabel htmlFor="earth-electrode-type">Type</FormLabel>
                        <ComboboxInput
                          id="earth-electrode-type"
                          name="earth_electrode_type"
                          value={supply.particulars.earthElectrode.type}
                          onChange={(e) => setSupply(prev => ({
                            ...prev,
                            particulars: {
                              ...prev.particulars,
                              earthElectrode: { ...prev.particulars.earthElectrode, type: e.target.value }
                            }
                          }))}
                          options={['Rod', 'Mat', 'Disc', 'Tape', 'Plate', 'Structure', 'LIM', 'Unknown', 'N/A']}
                        />
                      </div>

                      {/* Resistance to earth */}
                      <div>
                        <FormLabel htmlFor="resistance-to-earth">Resistance to earth</FormLabel>
                        <input
                          type="text"
                          id="earth-electrode-resistance"
                          name="earth_electrode_resistance"
                          value={supply.particulars.earthElectrode.resistance}
                          onChange={(e) => setSupply(prev => ({
                            ...prev,
                            particulars: {
                              ...prev.particulars,
                              earthElectrode: { ...prev.particulars.earthElectrode, resistance: e.target.value }
                            }
                          }))}
                          className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter resistance"
                        />
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {['N/A', 'LIM'].map(v => (
                            <button
                              key={v}
                              type="button"
                              onClick={() => setSupply(prev => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  earthElectrode: { ...prev.particulars.earthElectrode, resistance: v }
                                }
                              }))}
                              className="inline-flex items-center justify-center px-2 py-1 text-[11px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900"
                            >
                              {v}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Location */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                        <input
                          type="text"
                          id="earth-electrode-location"
                          name="earth_electrode_location"
                          value={supply.particulars.earthElectrode.location}
                          onChange={(e) => setSupply(prev => ({
                            ...prev,
                            particulars: {
                              ...prev.particulars,
                              earthElectrode: { ...prev.particulars.earthElectrode, location: e.target.value }
                            }
                          }))}
                          className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter location"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <hr className="w-full border-gray-300 dark:border-gray-600 mb-4" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <div className="md:pr-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Main protective bonding conductors</h4>
                      <div className="grid grid-cols-3 gap-2 items-end">
                        <div className="flex flex-col gap-1 min-w-0 w-full">
                          <FormLabel htmlFor="bonding-conductor-material" spacing={false}>Material</FormLabel>
                          <Select id="bonding-conductor-material" value={supply.particulars.bondingConductors.material} onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, bondingConductors:{...prev.particulars.bondingConductors, material:e.target.value}}}))} className="w-full h-8 text-xs">
                            <option value="">Select…</option>
                            <option value="copper">Copper</option>
                            <option value="aluminium">Aluminium</option>
                          </Select>
                        </div>
                        <div className="flex flex-col gap-1 min-w-0 w-full">
                          <FormLabel htmlFor="bonding-conductor-csa" spacing={false}>C.S.A (mm2)</FormLabel>
                          <ComboboxInput
                            id="bonding-conductor-csa"
                            name="bonding_conductor_csa"
                            value={supply.particulars.bondingConductors.csa}
                            onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, bondingConductors:{...prev.particulars.bondingConductors, csa:e.target.value}}}))}
                            options={EARTH_CONDUCTOR_CSA_OPTIONS}
                          />
                        </div>
                        <div className="flex flex-col gap-1 min-w-0 w-full">
                          <FormLabel htmlFor="bonding-conductor-continuity" spacing={false}>Continuity check</FormLabel>
                          <div className="flex w-full rounded-md shadow-sm divide-x divide-gray-200 dark:divide-gray-700">
                            {['PASS', 'FAIL', 'N/A', 'LIM'].map((v, idx, arr) => {
                              const isSelected = supply.particulars.bondingConductors.continuity === v;
                              const rounding = idx === 0 ? 'rounded-l-md' : idx === arr.length - 1 ? 'rounded-r-md' : '';
                              const classes = isSelected
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
                                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600';
                              return (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => setSupply(prev => ({
                                    ...prev,
                                    particulars: {
                                      ...prev.particulars,
                                      bondingConductors: { ...prev.particulars.bondingConductors, continuity: v },
                                    },
                                  }))}
                                  className={`px-1 py-2 text-[10px] inline-flex items-center justify-center flex-1 border border-gray-300 dark:border-gray-600 ${rounding} ${classes}`}
                                >
                                  {v}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="md:border-l border-gray-300 dark:border-gray-600 md:pl-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Supply conductors</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <FormLabel htmlFor="conductor-material">Conductor material</FormLabel>
                          <Select id="conductor-material" value={supply.particulars.mainSwitch.conductorMaterial} onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, conductorMaterial:e.target.value}}}))} className="w-full h-8">
                            <option value="">Select…</option>
                            <option value="copper">Copper</option>
                            <option value="aluminium">Aluminium</option>
                          </Select>
                        </div>
                        <div>
                          <FormLabel htmlFor="conductor-csa">Conductor CSA (mm2)</FormLabel>
                          <ComboboxInput
                            id="main-switch-conductor-csa"
                            name="main_switch_conductor_csa"
                            value={supply.particulars.mainSwitch.conductorCsa}
                            onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, conductorCsa:e.target.value}}}))}
                            options={CONDUCTOR_CSA_OPTIONS}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <hr className="w-full border-gray-300 dark:border-gray-600 mb-4" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Main switch / fuse / RCD</h3>
                </div>
                
                {/* Main switch / fuse / RCD */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-[0.5fr_1.5fr_1fr_1fr_1fr] gap-4 items-end">
                  <div className="min-w-0">
                    <FormLabel htmlFor="main-switch-type">Type BS(EN)</FormLabel>
                    <ComboboxInput
                      id="main-switch-type-bsen"
                      name="main_switch_type_bsen"
                      value={supply.particulars.mainSwitch.typeBsEn}
                      onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, typeBsEn:e.target.value}}}))}
                      options={DEVICE_STANDARD_OPTIONS}
                    />
                  </div>
                  <div className="min-w-0">
                    <FormLabel htmlFor="main-switch-location">Main switch location</FormLabel>
                    <Input id="main-switch-location" type="text" value={supply.particulars.mainSwitch.location} onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, location:e.target.value}}}))} className="w-full h-8" />
                  </div>
                  <div className="min-w-0">
                    <FormLabel htmlFor="main-switch-poles">No of poles</FormLabel>
                    <ComboboxInput
                      id="main-switch-poles"
                      name="main_switch_poles"
                      value={supply.particulars.mainSwitch.poles}
                      onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, poles:e.target.value}}}))}
                      options={MAIN_SWITCH_POLES_OPTIONS}
                    />
                  </div>
                  <div className="min-w-0">
                    <FormLabel htmlFor="main-switch-voltage-rating">Voltage rating</FormLabel>
                    <ComboboxInput
                      id="main-switch-voltage-rating"
                      name="main_switch_voltage_rating"
                      value={supply.particulars.mainSwitch.voltageRating}
                      onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, voltageRating:e.target.value}}}))}
                      options={VOLTAGE_RATING_OPTIONS}
                    />
                  </div>
                  <div className="min-w-0">
                    <FormLabel htmlFor="main-switch-rated-current">Rated current (In)</FormLabel>
                    <ComboboxInput
                      id="main-switch-rated-current-in"
                      name="main_switch_rated_current_in"
                      value={supply.particulars.mainSwitch.ratedCurrentIn}
                      onChange={(e)=>setSupply(prev=>({...prev, particulars:{...prev.particulars, mainSwitch:{...prev.particulars.mainSwitch, ratedCurrentIn:e.target.value}}}))}
                      options={SPD_RATED_CURRENT_A_OPTIONS}
                    />
                  </div>
                </div>

                <div className="mt-4 space-y-4">
                  {(!supply.particulars.overcurrentDevice.enabled || !supply.particulars.rcd.enabled) && (
                    <div>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        {!supply.particulars.overcurrentDevice.enabled && (
                          <button
                            type="button"
                            onClick={() => setSupply((prev) => ({
                              ...prev,
                              particulars: {
                                ...prev.particulars,
                                overcurrentDevice: { ...prev.particulars.overcurrentDevice, enabled: true },
                              },
                            }))}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-sm"
                          >
                            + Add overcurrent device details
                          </button>
                        )}
                        {!supply.particulars.rcd.enabled && (
                          <button
                            type="button"
                            onClick={() => setSupply((prev) => ({
                              ...prev,
                              particulars: {
                                ...prev.particulars,
                                rcd: { ...prev.particulars.rcd, enabled: true },
                              },
                            }))}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-sm"
                          >
                            + Add RCD details
                          </button>
                        )}
                      </div>
                      <p className="mt-2 text-sm italic text-gray-500 dark:text-gray-400">
                        If not added, these fields will appear as N/A on the PDF output.
                      </p>
                    </div>
                  )}

                  {supply.particulars.overcurrentDevice.enabled && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Shield className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Overcurrent protective device</h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSupply((prev) => ({
                            ...prev,
                            particulars: {
                              ...prev.particulars,
                              overcurrentDevice: { ...EMPTY_OVERCURRENT_DEVICE },
                            },
                          }))}
                          className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-sm"
                        >
                          <X className="h-4 w-4" />
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <FormLabel htmlFor="overcurrent-device-type" spacing={false}>Device type</FormLabel>
                          <ComboboxInput
                            id="overcurrent-device-type"
                            name="overcurrent_device_type"
                            value={supply.particulars.overcurrentDevice.deviceType}
                            onChange={(e) => setSupply((prev) => ({
                              ...prev,
                              particulars: {
                                ...prev.particulars,
                                overcurrentDevice: {
                                  ...prev.particulars.overcurrentDevice,
                                  deviceType: e.target.value,
                                },
                              },
                            }))}
                            options={DEVICE_STANDARD_OPTIONS}
                          />
                        </div>
                        <div>
                          <FormLabel htmlFor="overcurrent-device-rating" spacing={false}>Device rating / setting</FormLabel>
                          <div className="relative">
                            <ComboboxInput
                              id="overcurrent-device-rating"
                              name="overcurrent_device_rating"
                              value={supply.particulars.overcurrentDevice.deviceRatingSetting}
                              onChange={(e) => setSupply((prev) => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  overcurrentDevice: {
                                    ...prev.particulars.overcurrentDevice,
                                    deviceRatingSetting: e.target.value,
                                  },
                                },
                              }))}
                              options={OVERCURRENT_DEVICE_RATING_OPTIONS}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">A</span>
                          </div>
                        </div>
                        <div>
                          <FormLabel htmlFor="overcurrent-breaking-capacity" spacing={false}>Breaking capacity</FormLabel>
                          <div className="relative">
                            <ComboboxInput
                              id="overcurrent-breaking-capacity"
                              name="overcurrent_breaking_capacity"
                              value={supply.particulars.overcurrentDevice.breakingCapacity}
                              onChange={(e) => setSupply((prev) => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  overcurrentDevice: {
                                    ...prev.particulars.overcurrentDevice,
                                    breakingCapacity: e.target.value,
                                  },
                                },
                              }))}
                              options={SHORT_CIRCUIT_CAPACITY_KA_OPTIONS}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">kA</span>
                          </div>
                        </div>
                      </div>
                      {!supply.particulars.overcurrentDevice.settingsEnabled ? (
                        <button
                          type="button"
                          onClick={() => setSupply((prev) => ({
                            ...prev,
                            particulars: {
                              ...prev.particulars,
                              overcurrentDevice: {
                                ...prev.particulars.overcurrentDevice,
                                settingsEnabled: true,
                              },
                            },
                          }))}
                          className="mt-4 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-sm"
                        >
                          + Attach protective device settings
                        </button>
                      ) : (
                        <div className="mt-4">
                          <FormLabel htmlFor="overcurrent-protective-device-settings" spacing={false}>
                            Protective device settings
                          </FormLabel>
                          <Textarea
                            id="overcurrent-protective-device-settings"
                            rows={4}
                            value={supply.particulars.overcurrentDevice.protectiveDeviceSettings}
                            onChange={(e) => setSupply((prev) => ({
                              ...prev,
                              particulars: {
                                ...prev.particulars,
                                overcurrentDevice: {
                                  ...prev.particulars.overcurrentDevice,
                                  protectiveDeviceSettings: e.target.value,
                                },
                              },
                            }))}
                            className="mt-1 min-h-[100px] border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <div className="mt-2 flex items-center justify-between gap-4">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Note any protective device settings where applicable
                            </p>
                            <button
                              type="button"
                              onClick={() => setSupply((prev) => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  overcurrentDevice: {
                                    ...prev.particulars.overcurrentDevice,
                                    settingsEnabled: false,
                                  },
                                },
                              }))}
                              className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-sm flex-shrink-0"
                            >
                              <MinusCircle className="h-4 w-4" />
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {supply.particulars.rcd.enabled && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Waves className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">RCD</h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSupply((prev) => ({
                            ...prev,
                            particulars: {
                              ...prev.particulars,
                              rcd: { ...EMPTY_RCD_DETAILS },
                            },
                          }))}
                          className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 rounded-sm"
                        >
                          <X className="h-4 w-4" />
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                          <FormLabel htmlFor="supply-rcd-type" spacing={false}>RCD type</FormLabel>
                          <ComboboxInput
                            id="supply-rcd-type"
                            name="supply_rcd_type"
                            value={supply.particulars.rcd.type}
                            onChange={(e) => setSupply((prev) => ({
                              ...prev,
                              particulars: {
                                ...prev.particulars,
                                rcd: { ...prev.particulars.rcd, type: e.target.value },
                              },
                            }))}
                            options={RCD_TYPE_OPTIONS}
                          />
                        </div>
                        <div>
                          <FormLabel htmlFor="supply-rcd-rated-residual-current" spacing={false}>Rated residual current IΔn</FormLabel>
                          <ComboboxInput
                            id="supply-rcd-rated-residual-current"
                            name="supply_rcd_rated_residual_current"
                            value={supply.particulars.rcd.ratedResidualCurrent}
                            onChange={(e) => setSupply((prev) => ({
                              ...prev,
                              particulars: {
                                ...prev.particulars,
                                rcd: { ...prev.particulars.rcd, ratedResidualCurrent: e.target.value },
                              },
                            }))}
                            options={RCD_OPERATING_CURRENT_OPTIONS}
                            placeholder="Select… mA"
                          />
                        </div>
                        <div>
                          <FormLabel htmlFor="supply-rcd-breaking-capacity" spacing={false}>Breaking capacity</FormLabel>
                          <div className="relative">
                            <ComboboxInput
                              id="supply-rcd-breaking-capacity"
                              name="supply_rcd_breaking_capacity"
                              value={supply.particulars.rcd.breakingCapacity}
                              onChange={(e) => setSupply((prev) => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  rcd: { ...prev.particulars.rcd, breakingCapacity: e.target.value },
                                },
                              }))}
                              options={SHORT_CIRCUIT_CAPACITY_KA_OPTIONS}
                              className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">kA</span>
                          </div>
                        </div>
                        <div>
                          <FormLabel htmlFor="supply-rcd-rated-time-delay" spacing={false}>Rated time delay</FormLabel>
                          <div className="relative">
                            <Input
                              id="supply-rcd-rated-time-delay"
                              type="text"
                              value={supply.particulars.rcd.ratedTimeDelay}
                              onChange={(e) => setSupply((prev) => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  rcd: { ...prev.particulars.rcd, ratedTimeDelay: e.target.value },
                                },
                              }))}
                              className="w-full h-8 pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">ms</span>
                          </div>
                        </div>
                        <div>
                          <FormLabel htmlFor="supply-rcd-measured-operating-time" spacing={false}>Measured operating time</FormLabel>
                          <div className="relative">
                            <Input
                              id="supply-rcd-measured-operating-time"
                              type="text"
                              value={supply.particulars.rcd.measuredOperatingTime}
                              onChange={(e) => setSupply((prev) => ({
                                ...prev,
                                particulars: {
                                  ...prev.particulars,
                                  rcd: { ...prev.particulars.rcd, measuredOperatingTime: e.target.value },
                                },
                              }))}
                              className="w-full h-8 pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">ms</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <hr className="w-full border-gray-300 dark:border-gray-600 mb-4" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Bonding of extraneous conductive parts</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                    {EXTRANEOUS_BONDING_ITEMS.map(({ label, key, editableLabel }) => {
                      const Icon = EXTRANEOUS_ICON_MAP[key] ?? Info;
                      const displayLabel = editableLabel ? extraneousOtherDisplayLabel : label;
                      return (
                        <div key={key} className="rounded-md">
                          <div className="px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 min-w-0">
                            <Icon className="h-4 w-4 text-gray-500 dark:text-gray-300 flex-shrink-0" />
                            <span className="truncate">{displayLabel}</span>
                            {editableLabel && (
                              <button
                                type="button"
                                onClick={handleOpenExtraneousOtherLabelModal}
                                className="inline-flex items-center justify-center h-6 w-6 rounded-md text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                                aria-label="Rename other bonding item"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="px-2 pb-2">
                            <div className="inline-flex flex-col w-full max-w-[110px] rounded-md shadow-sm">
                              {EXTRANEOUS_BONDING_STATUS.map((v, idx, arr) => {
                                const isSelected = supply.particulars.extraneousBonding[key] === v;
                                const selectedClasses = v === 'PASS'
                                  ? 'border-green-600 text-green-700 bg-green-50 dark:bg-green-900/30'
                                  : v === 'FAIL'
                                    ? 'border-red-600 text-red-700 bg-red-50 dark:bg-red-900/30'
                                    : v === 'LIM'
                                      ? 'border-orange-500 text-orange-600 bg-orange-50 dark:bg-orange-900/30'
                                      : 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/30';
                                const baseClasses = 'border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600';
                                const rounding =
                                  idx === 0
                                    ? 'rounded-t-md'
                                    : idx === arr.length - 1
                                      ? 'rounded-b-md'
                                      : '';
                                const borderClass = isSelected
                                  ? `${selectedClasses} ${idx !== arr.length - 1 ? 'border-b' : ''}`
                                  : `${baseClasses} ${idx !== arr.length - 1 ? 'border-b-0' : ''}`;
                                return (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={() => setSupply((prev) => ({
                                      ...prev,
                                      particulars: {
                                        ...prev.particulars,
                                        extraneousBonding: {
                                          ...prev.particulars.extraneousBonding,
                                          [key]: v,
                                        },
                                      },
                                    }))}
                                    className={`px-3 py-1.5 text-sm border first:border-t last:border-b inline-flex justify-center ${rounding} ${borderClass}`}
                                  >
                                    {v}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('declaration')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Declaration</button>
                <button type="button" onClick={() => handleChangeTab('inspection_schedule')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Next: Inspection Schedule →</button>
              </div>
            </section>
          )}

          {activeTab === 'inspection_schedule' && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Inspection Schedule</h2>
                <button
                  type="button"
                  onClick={() => {
                    const allSectionIds = inspection.sections.map(s => s.id);
                    if (expandedSections.length === allSectionIds.length) {
                      // Collapse all
                      setExpandedSections([]);
                    } else {
                      // Expand all
                      setExpandedSections(allSectionIds);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {expandedSections.length === inspection.sections.length ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Expand All
                    </>
                  )}
                </button>
              </div>

              <Accordion 
                type="multiple" 
                value={expandedSections}
                onValueChange={setExpandedSections}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
              >
                {inspection.sections.map((section) => (
                  <AccordionItem key={section.id} value={section.id}>
                    <AccordionTrigger 
                      className="bg-gray-50 dark:bg-gray-900/40"
                      rightContent={
                        <div className="flex items-center gap-2">
                          <button 
                            type="button" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setInspection(prev => ({
                                ...prev,
                                sections: prev.sections.map(s => s.id === section.id ? {
                                  ...s,
                                  items: s.items.map(it => ({ ...it, result: 'OK' }))
                                } : s)
                              }));
                            }} 
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                          >
                            ✓ Pass
                          </button>
                          <button 
                            type="button" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setInspection(prev => ({
                                ...prev,
                                sections: prev.sections.map(s => s.id === section.id ? {
                                  ...s,
                                  items: s.items.map(it => ({ ...it, result: 'NA' }))
                                } : s)
                              }));
                            }} 
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-md transition-colors"
                          >
                            N/A
                          </button>
                        </div>
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center h-5 w-10 text-xs font-semibold rounded bg-blue-600 text-white">{section.code}</span>
                        <span className="text-sm">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-gray-600 dark:text-gray-300">
                              <th className="text-left py-3 pr-3">Item</th>
                              {INSPECTION_OPTIONS.map(opt => (
                                <th key={opt.key} className="px-2 py-3 text-center font-medium">{opt.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {section.items.map((item, idx) => {
                              // Calculate indentation level based on number of dots in code
                              // e.g., "5.4" = 1 dot = level 0 (no indent), "5.4.1" = 2 dots = level 1 (indent)
                              const codeParts = (item.code || '').split('.').length;
                              const indentLevel = Math.max(0, codeParts - 2); // Level 0 for x.x, level 1 for x.x.x, etc.
                              const indentPx = indentLevel * 16; // 16px (1rem) per level
                              
                              return (
                              <tr key={item.id} className="align-top">
                                <td className="py-3 pr-3">
                                  <div 
                                    className="text-gray-900 dark:text-gray-100 font-medium"
                                    style={indentLevel > 0 ? { paddingLeft: `${indentPx}px` } : {}}
                                  >
                                    {item.code} {item.text}
                                  </div>
                                </td>
                                {INSPECTION_OPTIONS.map(opt => (
                                  <td key={opt.key} className="px-2 py-3 text-center">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // set result
                                        setInspection(prev => ({
                                          ...prev,
                                          sections: prev.sections.map(s => s.id === section.id ? ({
                                            ...s,
                                            items: s.items.map((it) => it.id === item.id ? ({ ...it, result: opt.key }) : it)
                                          }) : s)
                                        }));
                                        // inspection side-effects
                                        // Note: Limitation modal removed as operational limitations field is not used in EIC
                                      }}
                                      className={`h-8 w-8 inline-flex items-center justify-center text-xs font-semibold rounded-full border ${item.result===opt.key ? getInspectionActiveClasses(opt.key) : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700'}`}
                                    >
                                      {opt.label}
                                    </button>
                                  </td>
                                ))}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('supply_characteristics')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Supply Characteristics</button>
                <button type="button" onClick={() => handleChangeTab('boards_circuits')} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">Next: Boards & Circuits →</button>
              </div>
            </section>
          )}

          {/* Preset Drawer */}
          <BottomSheet
            open={presetDrawerOpen}
            onOpenChange={handlePresetDrawerChange}
            title={activePresetLabel ? `${activePresetLabel} presets` : 'Text presets'}
          >
            {activePresetField ? (
              <div className="space-y-6">
                <div>
                  {presetsForActiveField.length > 0 ? (
                    <div className="space-y-3">
                      {presetsForActiveField.map((preset) => (
                        <div key={preset.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800/80 shadow-sm">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="space-y-2">
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{preset.name}</div>
                              </div>
                              <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                                {preset.content}
                              </div>
                            </div>
                            <div className="flex flex-row sm:flex-col gap-2 sm:shrink-0">
                              <button
                                type="button"
                                onClick={() => handleApplyPreset(activePresetField, preset.content)}
                                className="inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm"
                              >
                                Use preset
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePreset(activePresetField, preset.id)}
                                className="inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center bg-white dark:bg-gray-800/80">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">No presets saved yet.</div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Create your first preset below to speed up future reports.</div>
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/40">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Save a new preset</h4>
                  {presetError && (
                    <p className="mt-2 text-xs text-red-500 dark:text-red-400">{presetError}</p>
                  )}
                  <div className="mt-3 space-y-3">
                    <div>
                      <FormLabel htmlFor="preset-name" className="text-gray-600 dark:text-gray-400">Preset name</FormLabel>
                      <Input
                        id="preset-name"
                        type="text"
                        value={presetForm.name}
                        onChange={(e) => {
                          setPresetForm((prev) => ({ ...prev, name: e.target.value }));
                          if (presetError) setPresetError('');
                        }}
                        className="w-full h-8"
                        placeholder="e.g. Partial inspection"
                      />
                    </div>
                    <div>
                      <FormLabel htmlFor="preset-text" className="text-gray-600 dark:text-gray-400">Preset text</FormLabel>
                      <Textarea
                        id="preset-text"
                        rows={4}
                        value={presetForm.content}
                        onChange={(e) => {
                          setPresetForm((prev) => ({ ...prev, content: e.target.value }));
                          if (presetError) setPresetError('');
                        }}
                        placeholder={PRESET_FIELD_CONFIG[activePresetField]?.placeholder}
                      />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setPresetForm((prev) => ({ ...prev, content: getFieldValueByKey(activePresetField) }));
                          setPresetError('');
                        }}
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none"
                      >
                        Use current text
                      </button>
                      <button
                        type="button"
                        onClick={handleSavePreset}
                        className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md shadow-sm"
                      >
                        Save preset
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-300">Select a field to manage presets.</div>
            )}
          </BottomSheet>


          {activeTab === 'boards_circuits' && (
            <section>
              {editingBoard === null ? (
                // List View
                <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Boards & Circuits</h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleOpenCertificateCircuitsManager}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Circuits
                  </button>
                  <button type="button" onClick={handleAddBoard} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-[#3778E2] bg-[#141821] text-[#3778E2] shadow-sm hover:bg-[#1a2332] hover:border-[#3778E2]">
                    + Add Board
                  </button>
                </div>
              </div>

              {/* Distribution Boards & Consumer Units */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                <div className="p-4 bg-[var(--ds-canvas-bg)]">
                  {boards.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-12 text-center">
                      <div className="text-5xl mb-2 text-gray-400">⚡</div>
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">No boards added yet</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add your first distribution board to get started</div>
                      <div className="mt-3">
                        <button type="button" onClick={handleAddBoard} className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600">
                          + Add Board
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {boards.map((b) => (
                        <div key={b.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{b.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{b.location || 'No location specified'}</div>
                          </div>
                          <button type="button" onClick={() => handleEditBoard(b)} className="px-3 py-1 rounded-md text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Edit</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('inspection_schedule')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Inspection Schedule</button>
                <button type="button" onClick={() => handleChangeTab('appendix_notes')} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">Next: Appendix & Notes →</button>
              </div>
                </>
              ) : (
                // Board Edit Form
                <div className="space-y-6">
                  {/* Top Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button type="button" onClick={() => setEditingBoard(null)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                      ← Back to Boards List
                    </button>
                    <div className="flex items-center gap-3">
                      {editingBoard && editingBoard !== 'new' && (
                        <button 
                          type="button" 
                          onClick={() => setShowDeleteBoardModal(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-700 rounded-md shadow-sm text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Board
                        </button>
                      )}
                      <button 
                        type="button" 
                        onClick={handleOpenCircuitsManager}
                        disabled={!boardDraft.name.trim()}
                        className={`px-4 py-2 rounded-md shadow-sm text-sm font-medium h-9 border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
                          boardDraft.name.trim()
                            ? 'border-[#3778E2] bg-[#141821] text-[#3778E2] hover:bg-[#1a2332] hover:border-[#3778E2] cursor-pointer'
                            : 'text-gray-400 bg-gray-300 dark:bg-gray-700 cursor-not-allowed border-gray-300'
                        }`}
                      >
                        Manage Circuits →
                      </button>
                    </div>
                  </div>

                  {/* Board Details Section */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Board Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <FormLabel htmlFor="board-name" required>
                          Name
                        </FormLabel>
                        <Input
                          id="board-name"
                          type="text"
                          value={boardDraft.name}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Main Distribution Board"
                          className="w-full h-8"
                        />
                      </div>

                      <div>
                        <FormLabel htmlFor="board-manufacturer">Manufacturer</FormLabel>
                        <Input
                          id="board-manufacturer"
                          type="text"
                          value={boardDraft.manufacturer}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, manufacturer: e.target.value }))}
                          placeholder="e.g., Hager, MK Electric"
                          className="w-full h-8"
                        />
                      </div>

                      <div>
                        <FormLabel htmlFor="board-location">Location</FormLabel>
                        <Input
                          id="board-location"
                          type="text"
                          value={boardDraft.location}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="e.g., Garage, Utility Room"
                          className="w-full h-8"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Board Photo</label>
                        <div className="flex items-center gap-4">
                          <label className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--ds-control-field-border)] rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-[var(--ds-control-field-bg)] hover:opacity-90 cursor-pointer">
                            <Camera className="w-4 h-4" />
                            Add Photo
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleBoardPhoto(e.target.files[0])}
                              className="hidden"
                            />
                          </label>
                          {boardDraft.photo && (
                            <div className="flex items-center gap-2">
                              <img src={boardDraft.photo.dataUrl} alt="Board" className="h-16 w-16 object-cover rounded border border-gray-300 dark:border-gray-600" />
                              <button
                                type="button"
                                onClick={() => setBoardDraft(prev => ({ ...prev, photo: null }))}
                                className="text-sm text-red-600 hover:text-red-800"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <FormLabel htmlFor="board-supplied-from">Supplied from</FormLabel>
                        <Input
                          id="board-supplied-from"
                          type="text"
                          value={boardDraft.suppliedFrom}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, suppliedFrom: e.target.value }))}
                          placeholder="e.g., Main DB, Sub-main"
                          className="w-full h-8"
                        />
                      </div>

                      <div>
                        <FormLabel htmlFor="board-num-phases">Number of phases</FormLabel>
                        <Select
                          id="board-num-phases"
                          value={boardDraft.numPhases}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, numPhases: e.target.value }))}
                          className="w-full h-8"
                        >
                          <option value="">Select...</option>
                          {NUM_PHASES.map(phase => (
                            <option key={phase.value} value={phase.value}>{phase.label}</option>
                          ))}
                        </Select>
                      </div>

                      <div>
                        <FormLabel className="mb-2">Supply polarity confirmed</FormLabel>
                        <div className="flex gap-2">
                          {CONFIRM_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, polarityConfirmed: opt.value }))}
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                boardDraft.polarityConfirmed === opt.value
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <FormLabel className="mb-2">Phase sequence confirmed</FormLabel>
                        <div className="flex gap-2">
                          {TEST_RESULT_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, phaseSequenceConfirmed: opt.value }))}
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                boardDraft.phaseSequenceConfirmed === opt.value
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <FormLabel htmlFor="board-zs-at-db">Zs at DB</FormLabel>
                        <Input
                          id="board-zs-at-db"
                          type="text"
                          value={boardDraft.zsAtDb}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, zsAtDb: e.target.value }))}
                          placeholder="Enter value"
                          className="w-full h-8"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => setBoardDraft(prev => ({ ...prev, zsAtDbNa: !prev.zsAtDbNa, zsAtDbLim: false }))}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              boardDraft.zsAtDbNa
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            N/A
                          </button>
                          <button
                            type="button"
                            onClick={() => setBoardDraft(prev => ({ ...prev, zsAtDbLim: !prev.zsAtDbLim, zsAtDbNa: false }))}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              boardDraft.zsAtDbLim
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            LIM
                          </button>
                        </div>
                      </div>

                      <div>
                        <FormLabel htmlFor="board-ipf-at-db">Ipf at DB</FormLabel>
                        <Input
                          id="board-ipf-at-db"
                          type="text"
                          value={boardDraft.ipfAtDb}
                          onChange={(e) => setBoardDraft(prev => ({ ...prev, ipfAtDb: e.target.value }))}
                          placeholder="Enter value"
                          className="w-full h-8"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => setBoardDraft(prev => ({ ...prev, ipfAtDbNa: !prev.ipfAtDbNa, ipfAtDbLim: false }))}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              boardDraft.ipfAtDbNa
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            N/A
                          </button>
                          <button
                            type="button"
                            onClick={() => setBoardDraft(prev => ({ ...prev, ipfAtDbLim: !prev.ipfAtDbLim, ipfAtDbNa: false }))}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              boardDraft.ipfAtDbLim
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            LIM
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main Switch Section */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                      <h3 className="text-sm font-semibold">Main Switch</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BS(EN)</label>
                          <select
                            value={boardDraft.mainSwitch.bsEn}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, bsEn: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {BS_EN_STANDARDS.map(std => (
                              <option key={std.value} value={std.value}>{std.value}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Voltage rating (V)</label>
                          <select
                            value={boardDraft.mainSwitch.voltageRating}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, voltageRating: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {VOLTAGE_RATINGS.map(volt => (
                              <option key={volt.value} value={volt.value}>{volt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rated Current (A)</label>
                          <select
                            value={boardDraft.mainSwitch.ratedCurrent}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, ratedCurrent: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {RATED_CURRENT_OPTIONS.map(current => (
                              <option key={current.value} value={current.value}>{current.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ipf Rating (kA)</label>
                          <input
                            type="text"
                            value={boardDraft.mainSwitch.ipfRating}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, ipfRating: e.target.value } }))}
                            placeholder="Enter value"
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, ipfRatingNa: !prev.mainSwitch.ipfRatingNa, ipfRatingLim: false } }))}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                boardDraft.mainSwitch.ipfRatingNa
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              N/A
                            </button>
                            <button
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, ipfRatingLim: !prev.mainSwitch.ipfRatingLim, ipfRatingNa: false } }))}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                boardDraft.mainSwitch.ipfRatingLim
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              LIM
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RCD Rating (mA)</label>
                          <select
                            value={boardDraft.mainSwitch.rcdRating}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, rcdRating: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {RCD_RATINGS.map(rcd => (
                              <option key={rcd.value} value={rcd.value}>{rcd.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RCD trip time (ms)</label>
                          <input
                            type="text"
                            value={boardDraft.mainSwitch.rcdTripTime}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, rcdTripTime: e.target.value } }))}
                            placeholder="Enter value"
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, rcdTripTimeNa: !prev.mainSwitch.rcdTripTimeNa, rcdTripTimeLim: false } }))}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                boardDraft.mainSwitch.rcdTripTimeNa
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              N/A
                            </button>
                            <button
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, mainSwitch: { ...prev.mainSwitch, rcdTripTimeLim: !prev.mainSwitch.rcdTripTimeLim, rcdTripTimeNa: false } }))}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                boardDraft.mainSwitch.rcdTripTimeLim
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              LIM
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SPD Details Section */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                      <h3 className="text-sm font-semibold">SPD Details</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SPD Type</label>
                          <select
                            value={boardDraft.spd.type}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, spd: { ...prev.spd, type: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {SPD_TYPES.map(spd => (
                              <option key={spd.value} value={spd.value}>{spd.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Functional check</label>
                          <div className="flex gap-2">
                            {TEST_RESULT_OPTIONS.map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setBoardDraft(prev => ({ ...prev, spd: { ...prev.spd, functionalCheck: opt.value } }))}
                                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                  boardDraft.spd.functionalCheck === opt.value
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Overcurrent Device Section */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                      <h3 className="text-sm font-semibold">Overcurrent Device</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BS(EN)</label>
                          <select
                            value={boardDraft.overcurrentDevice.bsEn}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, overcurrentDevice: { ...prev.overcurrentDevice, bsEn: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {BS_EN_STANDARDS.map(std => (
                              <option key={std.value} value={std.value}>{std.value}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Voltage Rating</label>
                          <select
                            value={boardDraft.overcurrentDevice.voltageRating}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, overcurrentDevice: { ...prev.overcurrentDevice, voltageRating: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {VOLTAGE_RATINGS.map(volt => (
                              <option key={volt.value} value={volt.value}>{volt.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rated current</label>
                          <select
                            value={boardDraft.overcurrentDevice.ratedCurrent}
                            onChange={(e) => setBoardDraft(prev => ({ ...prev, overcurrentDevice: { ...prev.overcurrentDevice, ratedCurrent: e.target.value } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {RATED_CURRENT_OPTIONS.map(current => (
                              <option key={current.value} value={current.value}>{current.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Testing Information Section */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                      <h3 className="text-sm font-semibold">Testing Information</h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Engineer</label>
                          <select
                            value={boardDraft.testing.engineer}
                            onChange={async (e) => {
                              const selectedEngineer = e.target.value;
                              
                              // Extract user ID from engineer value
                              const userId = selectedEngineer.startsWith('user:') 
                                ? selectedEngineer.replace('user:', '') 
                                : selectedEngineer;
                              
                              // Fetch equipment for the selected engineer
                              let equipment = {
                                multifunctionMft: '',
                                continuity: '',
                                insulationResistance: '',
                                earthLoop: '',
                                rcd: '',
                                earthElectrode: '',
                              };
                              
                              if (userId && selectedEngineer) {
                                const equipmentData = await fetchEngineerEquipment(userId);
                                // Auto-populate equipment based on fetched data
                                equipment = {
                                  multifunctionMft: equipmentData.multifunctionMft?.[0] || 'N/A',
                                  continuity: equipmentData.continuity?.[0] || 'N/A',
                                  insulationResistance: equipmentData.insulationResistance?.[0] || 'N/A',
                                  earthLoop: equipmentData.earthLoop?.[0] || 'N/A',
                                  rcd: equipmentData.rcd?.[0] || 'N/A',
                                  earthElectrode: equipmentData.earthElectrode?.[0] || 'N/A',
                                };
                              }
                              
                              setBoardDraft(prev => ({ 
                                ...prev, 
                                testing: { 
                                  ...prev.testing, 
                                  engineer: selectedEngineer,
                                  equipment: equipment
                                } 
                              }));
                            }}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select engineer...</option>
                            {signatoryOptions
                              .filter(opt => opt.type === 'user')
                              .map(eng => (
                                <option key={eng.value} value={eng.value}>{eng.label}</option>
                              ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date tested</label>
                          <div className="flex gap-2">
                            <input
                              type="date"
                              value={boardDraft.testing.dateTested}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, dateTested: e.target.value } }))}
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, dateTested: new Date().toISOString().slice(0, 10) } }))}
                              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                            >
                              Today
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Equipment</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Multifunction (MFT)</label>
                            <select
                              value={boardDraft.testing.equipment.multifunctionMft}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, equipment: { ...prev.testing.equipment, multifunctionMft: e.target.value } } }))}
                            className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select equipment...</option>
                            {(() => {
                              const engineerEquipment = getEngineerEquipment(boardDraft.testing.engineer, 'multifunctionMft');
                                if (engineerEquipment.length === 0) {
                                  return <option value="N/A">N/A</option>;
                                }
                                return (
                                  <>
                                    {engineerEquipment.map(eq => (
                                      <option key={eq.value} value={eq.value}>{eq.label}</option>
                                    ))}
                                  </>
                                );
                              })()}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Continuity</label>
                            <select
                              value={boardDraft.testing.equipment.continuity}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, equipment: { ...prev.testing.equipment, continuity: e.target.value } } }))}
                              className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select equipment...</option>
                              {(() => {
                                const engineerEquipment = getEngineerEquipment(boardDraft.testing.engineer, 'continuity');
                                if (engineerEquipment.length === 0) {
                                  return <option value="N/A">N/A</option>;
                                }
                                return (
                                  <>
                                    {engineerEquipment.map(eq => (
                                      <option key={eq.value} value={eq.value}>{eq.label}</option>
                                    ))}
                                  </>
                                );
                              })()}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Insulation resistance</label>
                            <select
                              value={boardDraft.testing.equipment.insulationResistance}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, equipment: { ...prev.testing.equipment, insulationResistance: e.target.value } } }))}
                              className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select equipment...</option>
                              {(() => {
                                const engineerEquipment = getEngineerEquipment(boardDraft.testing.engineer, 'insulationResistance');
                                if (engineerEquipment.length === 0) {
                                  return <option value="N/A">N/A</option>;
                                }
                                return (
                                  <>
                                    {engineerEquipment.map(eq => (
                                      <option key={eq.value} value={eq.value}>{eq.label}</option>
                                    ))}
                                  </>
                                );
                              })()}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Earth loop</label>
                            <select
                              value={boardDraft.testing.equipment.earthLoop}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, equipment: { ...prev.testing.equipment, earthLoop: e.target.value } } }))}
                              className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select equipment...</option>
                              {(() => {
                                const engineerEquipment = getEngineerEquipment(boardDraft.testing.engineer, 'earthLoop');
                                if (engineerEquipment.length === 0) {
                                  return <option value="N/A">N/A</option>;
                                }
                                return (
                                  <>
                                    {engineerEquipment.map(eq => (
                                      <option key={eq.value} value={eq.value}>{eq.label}</option>
                                    ))}
                                  </>
                                );
                              })()}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RCD</label>
                            <select
                              value={boardDraft.testing.equipment.rcd}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, equipment: { ...prev.testing.equipment, rcd: e.target.value } } }))}
                              className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select equipment...</option>
                              {(() => {
                                const engineerEquipment = getEngineerEquipment(boardDraft.testing.engineer, 'rcd');
                                if (engineerEquipment.length === 0) {
                                  return <option value="N/A">N/A</option>;
                                }
                                return (
                                  <>
                                    {engineerEquipment.map(eq => (
                                      <option key={eq.value} value={eq.value}>{eq.label}</option>
                                    ))}
                                  </>
                                );
                              })()}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Earth Electrode</label>
                            <select
                              value={boardDraft.testing.equipment.earthElectrode}
                              onChange={(e) => setBoardDraft(prev => ({ ...prev, testing: { ...prev.testing, equipment: { ...prev.testing.equipment, earthElectrode: e.target.value } } }))}
                              className="w-full h-8 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select equipment...</option>
                              {(() => {
                                const engineerEquipment = getEngineerEquipment(boardDraft.testing.engineer, 'earthElectrode');
                                if (engineerEquipment.length === 0) {
                                  return <option value="N/A">N/A</option>;
                                }
                                return (
                                  <>
                                    {engineerEquipment.map(eq => (
                                      <option key={eq.value} value={eq.value}>{eq.label}</option>
                                    ))}
                                  </>
                                );
                              })()}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                      <h3 className="text-sm font-semibold">Notes</h3>
                    </div>
                    <div className="p-6">
                      <textarea
                        rows={6}
                        value={boardDraft.notes}
                        onChange={(e) => setBoardDraft(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add additional information about the CU/DB here..."
                        className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Bottom Navigation */}
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => setEditingBoard(null)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">← Back to Boards List</button>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleSaveBoard} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Save Board</button>
                      <button type="button" onClick={() => handleChangeTab('appendix_notes')} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">Next: Appendix & Notes →</button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {activeTab === 'appendix_notes' && (
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Appendix & Notes</h2>
              {/* Additional Page */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mb-6">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gray-50 dark:bg-gray-900/40">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Additional Page</h3>
                </div>
                <div className="p-4">
                  <FormLabel htmlFor="appendix-notes">Any additional content</FormLabel>
                  <Textarea
                    id="appendix-notes"
                    rows={4}
                    value={appendixNotes}
                    onChange={(e) => setAppendixNotes(e.target.value)}
                    placeholder="Enter any additional information to appear at the end of the certificate..."
                    className="text-xs"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This text will appear on an additional page at the end of the certificate, above any appendix photos.</p>
                </div>
              </div>

              {/* Appendix Photos */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Appendix Photos</h3>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                    + Upload Photo
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleAppendixFiles(e.target.files)}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="p-4">
                  {appendixPhotos.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-12 text-center">
                      <div className="text-5xl mb-2 text-gray-400">🖼️</div>
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">No photos added yet</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload your first photo to get started</div>
                      <div className="mt-3">
                        <label className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          Upload Photo
                          <input type="file" accept="image/*" multiple onChange={(e) => handleAppendixFiles(e.target.files)} className="hidden" />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {appendixPhotos.map((p, idx) => (
                        <div key={p.id} className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden bg-white dark:bg-gray-800">
                          <div className="relative">
                            <img src={p.dataUrl} alt={p.name} className="h-48 w-full object-cover" />
                            <div className="absolute top-2 right-2 flex gap-1">
                              <button onClick={() => setAppendixPhotos(prev => prev.filter(x => x.id !== p.id))} className="px-2 py-1 text-xs rounded bg-white/90 text-red-600">✕</button>
                            </div>
                          </div>
                          <div className="p-3 space-y-2">
                            <input
                              type="text"
                              value={p.title || ''}
                              onChange={(e) => setAppendixPhotos(prev => prev.map(x => x.id === p.id ? { ...x, title: e.target.value } : x))}
                              placeholder="Title (optional)"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                              type="text"
                              value={p.caption || ''}
                              onChange={(e) => setAppendixPhotos(prev => prev.map(x => x.id === p.id ? { ...x, caption: e.target.value } : x))}
                              placeholder="Caption (optional)"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={p.name}>{p.name}</span>
                              <div className="flex gap-2">
                                <button onClick={() => moveAppendixPhoto(p.id, 'up')} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300">↑</button>
                                <button onClick={() => moveAppendixPhoto(p.id, 'down')} className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300">↓</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Appendix Files */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl mt-6">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 rounded-t-xl bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Appendix Files</h3>
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                    + Upload Files
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                      multiple
                      onChange={(e) => handleAppendixFileUpload(e.target.files)}
                      className="hidden"
                    />
                  </label>
                </div>
                <div className="p-4">
                  {appendixFiles.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 p-12 text-center">
                      <div className="text-5xl mb-2 text-gray-400">📎</div>
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">No files added yet</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload PDFs, Microsoft Office documents, or images</div>
                      <div className="mt-3">
                        <label className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                          Upload Files
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp"
                            multiple
                            onChange={(e) => handleAppendixFileUpload(e.target.files)}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {appendixFiles.map((file, idx) => (
                        <div key={file.id} className="border border-gray-200 dark:border-gray-700 rounded-md p-4 bg-white dark:bg-gray-800 flex items-start gap-4">
                          <div className="text-4xl flex-shrink-0">{getFileIcon(file.extension)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={file.name}>
                                  {file.name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {formatFileSize(file.size)} • {file.extension.toUpperCase()}
                                </div>
                              </div>
                              <button
                                onClick={() => setAppendixFiles(prev => prev.filter(f => f.id !== file.id))}
                                className="px-2 py-1 text-xs rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 flex-shrink-0"
                                title="Remove file"
                              >
                                ✕
                              </button>
                            </div>
                            <input
                              type="text"
                              value={file.description || ''}
                              onChange={(e) => setAppendixFiles(prev => prev.map(f => f.id === file.id ? { ...f, description: e.target.value } : f))}
                              placeholder="Description (optional)"
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => moveAppendixFile(file.id, 'up')}
                                disabled={idx === 0}
                                className={`px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${
                                  idx === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveAppendixFile(file.id, 'down')}
                                disabled={idx === appendixFiles.length - 1}
                                className={`px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${
                                  idx === appendixFiles.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'
                                }`}
                              >
                                ↓
                              </button>
                              {file.dataUrl && (
                                <a
                                  href={file.dataUrl}
                                  download={file.name}
                                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                  Download
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6">
                <button type="button" onClick={() => handleChangeTab('boards_circuits')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">← Previous: Boards & Circuits</button>
                <button type="button" onClick={() => { saveDraft(); persistServerDraft(); }} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">Save Draft</button>
              </div>
            </section>
          )}
        </div>

        {/* Save Error Message */}
        {saveError && (
          <div className="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {saveError}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4 mt-6">
          <button
            type="button"
            onClick={() => navigate('/tenant/certification')}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveEic}
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save EIC'}
          </button>
        </div>

        {/* Status change modal */}
        {isStatusModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-xl border border-gray-200 dark:border-gray-700 shadow-xl">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Change certificate status</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    Current status: <span className="font-medium text-blue-600 dark:text-blue-300">{currentStatusOption.label}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCloseStatusModal}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label="Close status dialog"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 space-y-3">
                {STATUS_OPTIONS.map((option) => {
                  const isActive = certificateStatus === option.value;
                  const lacksPermission = option.requiresPermission && !canManageRestrictedStatuses;
                  const disabled = statusUpdating;
                  const OptionIcon = option.Icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleStatusSelect(option.value)}
                      disabled={disabled}
                      aria-disabled={lacksPermission}
                      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                        isActive
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                      } ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${
                        lacksPermission ? 'opacity-70' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <OptionIcon
                          className={`h-5 w-5 ${
                            isActive ? 'text-blue-600 dark:text-blue-300' : lacksPermission ? 'text-amber-500 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'
                          }`}
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-inherit">{option.label}</p>
                                {option.requiresPermission && (
                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                    canManageRestrictedStatuses
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                  }`}>
                                    QS/Admin
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{option.description}</p>
                            </div>
                            {isActive && (
                              <span className="text-xs font-semibold text-blue-600 dark:text-blue-300">Current</span>
                            )}
                          </div>
                          {lacksPermission && (
                            <p className="mt-2 text-xs text-amber-600 dark:text-amber-300">Requires QS or Admin access.</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {statusError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{statusError}</p>
                )}
              </div>
              <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
                {statusUpdating ? (
                  <span className="text-sm text-gray-600 dark:text-gray-300">Updating status...</span>
                ) : (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Select a new status to apply.</span>
                )}
                <button
                  type="button"
                  onClick={handleCloseStatusModal}
                  disabled={statusUpdating}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Client picker modal */}
        {showClientPicker && (
          <ClientPicker
            onSelect={(client) => {
              setSelectedClient(client);
              setClientId(client.id);
              if (useClientAddress) {
                const parts = [client.address_line1, client.address_line2, client.city, client.county, client.postcode, client.country].filter(Boolean);
                setInstallationAddress(parts.join(', '));
              }
              saveDraft();
            }}
            onClose={() => setShowClientPicker(false)}
            showModal
          />
        )}

        {/* Add Client Modal */}
        {showAddClientModal && (
          <AddClientModal
            onClose={() => setShowAddClientModal(false)}
            onSuccess={async (client) => {
              setShowAddClientModal(false);
              // Reload clients list
              const response = await clientsAPI.getAll({ limit: 100 });
              setClients(response.clients || []);
              // Auto-select the newly created client
              setSelectedClient(client);
              setClientId(client.id);
              if (useClientAddress && client) {
                const parts = [client.address_line1, client.address_line2, client.city, client.county, client.postcode, client.country].filter(Boolean);
                setInstallationAddress(parts.join(', '));
              }
              saveDraft();
            }}
          />
        )}



        {/* Delete Board Confirmation Modal */}
        {showDeleteBoardModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md border border-gray-200 dark:border-gray-700">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Board</h3>
                <button onClick={() => setShowDeleteBoardModal(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">✕</button>
              </div>
              <div className="p-5">
                <p className="text-base text-gray-700 dark:text-gray-300 text-center">
                  Are you sure you want to delete this board? This cannot be undone.
                </p>
              </div>
              <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
                <button onClick={() => setShowDeleteBoardModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">Cancel</button>
                <button onClick={handleDeleteBoard} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700">Delete Board</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={showPdfModal}
        onClose={handleClosePdfModal}
        pdfUrl={pdfUrl}
        certificateNum={uid || certificateNumber}
      />

      {/* Maximum Demand Help Slide-Over */}
      {maxDemandSlideOverOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={() => setMaxDemandSlideOverOpen(false)}
          />
          {/* Slide-over panel */}
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Maximum Demand</h2>
                <button
                  onClick={() => setMaxDemandSlideOverOpen(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {/* Content will be added later */}
                  
                  <header class="border-gray-200 dark:border-gray-700 pb-3">
                    <h2 class="text-lg font-semibold tracking-tight">
                      Maximum Demand – Quick Guide
                    </h2>
                    <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      Short practical guide to estimating maximum demand and applying diversity.
                    </p>
                  </header>

                  
                  <section class="space-y-2">
                    <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      What is Maximum Demand?
                    </h3>
                    <p>
                      Maximum demand (MD) is the highest expected load an installation will draw under normal
                      operating conditions. You are not adding every circuit at 100% – you apply
                      <span class="font-semibold">diversity</span> based on how the installation is likely to be used.
                    </p>
                    <ul class="mt-2 list-disc list-inside space-y-1">
                      <li>Check main fuse / service head suitability</li>
                      <li>Check supply capacity</li>
                      <li>Check CU rating / busbar loading</li>
                      <li>Size mains and submains correctly</li>
                    </ul>
                  </section>

                  <hr className="my-6 border-gray-300 dark:border-gray-600" />
                  <section class="space-y-3">
                    <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      Basic Method (Typical Domestic)
                    </h3>

                    <div>
                      <p class="font-semibold text-xs uppercase text-gray-600 dark:text-gray-400">
                        1. Start with the connected load
                      </p>
                      <p>
                        Add the total design current or kW of all circuits <span class="italic">before</span> applying any diversity.
                      </p>
                    </div>

                    <div>
                      <p class="font-semibold text-xs uppercase text-gray-600 dark:text-gray-400">
                        2. Apply diversity
                      </p>
                      <p class="mb-2">
                        Use practical diversity based on the IET On-Site Guide (OSG – Table A2) and BS 7671 guidance:
                      </p>

                      <div class="grid grid-cols-1 gap-1">
                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">Lighting</p>
                          <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            OSG Table A2
                          </p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li>66% of total, or</li>
                            <li>100% of largest + 66% of remainder</li>
                          </ul>
                        </div>

                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">Socket Circuits</p>
                          <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            Rings and radials for general use
                          </p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li>Largest ring at 100%</li>
                            <li>Each additional ring at 40%</li>
                            <li>Radials – apply similar judgement to rings</li>
                          </ul>
                        </div>

                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">Cooker / Hob</p>
                          <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            OSG cooker diversity formula
                          </p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li>First 10&nbsp;A</li>
                            <li>+ 30% of remainder</li>
                            <li>+ 5&nbsp;A if there is an integral socket</li>
                          </ul>
                        </div>

                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">Electric Shower</p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li><span class="font-semibold">No diversity</span> – take full design current</li>
                          </ul>
                        </div>

                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">Immersion Heater</p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li>Normally 100%</li>
                            <li>If dual immersions with proper time/priority controls, standby may be ignored</li>
                          </ul>
                        </div>

                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">Heating / AC</p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li>Fixed space heating: usually no diversity</li>
                            <li>Storage heaters: follow manufacturer / scheme guidance</li>
                          </ul>
                        </div>

                        
                        <div class="rounded-md p-3">
                          <p class="font-semibold">EV Chargers</p>
                          <ul class="mt-1 list-disc list-inside space-y-1">
                            <li>Generally <span class="font-semibold">no diversity</span></li>
                            <li>Only reduce if compliant load management is in place and designed accordingly</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p class="font-semibold text-xs uppercase text-gray-600 dark:text-gray-400">
                        3. Add everything together
                      </p>
                      <p>
                        Once diversity is applied to each circuit, sum the adjusted currents. This total is your
                        <span class="font-semibold">maximum demand</span>.
                      </p>
                      <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        Many domestic installs land roughly in the 40–80&nbsp;A range, but EVs, electric heating, heat pumps and hot tubs can push this much higher.
                      </p>
                    </div>
                  </section>

                  
                  <section class="space-y-2">
                    <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      Quick Domestic Example
                    </h3>

                    <div class="rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/40 p-3 space-y-2">
                      <div>
                        <p class="text-xs font-semibold uppercase text-gray-600 dark:text-gray-400">
                          Circuits
                        </p>
                        <ul class="mt-1 list-disc list-inside space-y-1">
                          <li>Ring 1: 32&nbsp;A</li>
                          <li>Ring 2: 32&nbsp;A</li>
                          <li>Lighting: 6&nbsp;A</li>
                          <li>Cooker: 10.2&nbsp;kW ≈ 44&nbsp;A</li>
                          <li>Shower: 9.5&nbsp;kW ≈ 41&nbsp;A</li>
                        </ul>
                      </div>

                      <div>
                        <p class="text-xs font-semibold uppercase text-gray-600 dark:text-gray-400">
                          Apply diversity
                        </p>
                        <ul class="mt-1 list-disc list-inside space-y-1">
                          <li>Ring 1: 32&nbsp;A</li>
                          <li>Ring 2: 40% → 12.8&nbsp;A</li>
                          <li>Lighting: ~6&nbsp;A (or 66% rule)</li>
                          <li>Cooker: 10&nbsp;A + 0.3 × 34&nbsp;A = 20.2&nbsp;A</li>
                          <li>Shower: 41&nbsp;A (no diversity)</li>
                        </ul>
                      </div>

                      <div class="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <p class="text-xs font-semibold uppercase text-gray-600 dark:text-gray-400">
                          Approximate MD
                        </p>
                        <p class="mt-1">
                          32 + 12.8 + 6 + 20.2 + 41 ≈
                          <span class="font-semibold">112&nbsp;A</span>  
                        </p>
                        <p class="mt-1 text-xs text-gray-600 dark:text-gray-400">
                          Adding an EV charger on top of this could easily push the MD to 150&nbsp;A+, likely requiring a supply upgrade or load management.
                        </p>
                      </div>
                    </div>
                  </section>

                  
                  <section class="space-y-2">
                    <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      Reference Documents
                    </h3>
                    <ul class="list-disc list-inside space-y-1">
                      <li>
                        <span class="font-semibold">BS 7671:2018+A2:2022</span> – Section 311 (Maximum demand &amp; diversity),
                        Appendix A (informative guidance).
                      </li>
                      <li>
                        <span class="font-semibold">IET On-Site Guide (OSG)</span> – Table A2 for practical diversity values.
                      </li>
                      <li>
                        <span class="font-semibold">IET Guidance Note 1</span> – Further detail on selection, erection and diversity.
                      </li>
                    </ul>
                  </section>

                  
                  <section class="space-y-2">
                    <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      Key Reminders
                    </h3>
                    <ul class="list-disc list-inside space-y-1">
                      <li>Diversity figures are <span class="font-semibold">guidance</span>, not mandatory.</li>
                      <li>Always consider the actual usage and any large loads (EVs, heat pumps, hot tubs, etc.).</li>
                      <li>Record your assumptions on the design and on the certificate.</li>
                      <li>If MD is borderline, state this and consider supply upgrade or load management.</li>
                    </ul>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </Layout>
  );
}

// Add Client Modal Component
function AddClientModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    client_type: 'individual',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
    credit_limit: '',
    current_balance: '',
    notes: '',
    is_active: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const newClient = await clientsAPI.create(formData);
      onSuccess(newClient);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create client');
      console.error('Form error:', err);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full my-8">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-lg z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">New Client</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Client Type Selection */}
          <div>
            <FormLabel className="mb-2" required>
              Client Type
            </FormLabel>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="client_type"
                  value="individual"
                  checked={formData.client_type === 'individual'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="flex items-center gap-1"><User className="h-4 w-4" /> Individual</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="client_type"
                  value="company"
                  checked={formData.client_type === 'company'}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="flex items-center gap-1"><Building2 className="h-4 w-4" /> Company</span>
              </label>
            </div>
          </div>

          {/* Company Fields */}
          {formData.client_type === 'company' && (
            <div>
              <FormLabel htmlFor="company_name" required>
                Company Name
              </FormLabel>
              <Input
                id="company_name"
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
                className="w-full h-8"
                placeholder="Smith Electrical Ltd"
              />
            </div>
          )}

          {/* Individual Fields */}
          {formData.client_type === 'individual' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FormLabel htmlFor="first_name" required>
                  First Name
                </FormLabel>
                <Input
                  id="first_name"
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full h-8"
                  placeholder="John"
                />
              </div>
              <div>
                <FormLabel htmlFor="last_name" required>
                  Last Name
                </FormLabel>
                <Input
                  id="last_name"
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full h-8"
                  placeholder="Smith"
                />
              </div>
            </div>
          )}

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full h-8"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <FormLabel htmlFor="phone">Phone</FormLabel>
              <Input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full h-8"
                placeholder="01234 567890"
              />
            </div>
            <div>
              <FormLabel htmlFor="mobile">Mobile</FormLabel>
              <Input
                id="mobile"
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className="w-full h-8"
                placeholder="07700 900123"
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Address</h3>
            <div>
              <FormLabel htmlFor="address_line1">Address Line 1</FormLabel>
              <Input
                id="address_line1"
                type="text"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                className="w-full h-8"
                placeholder="123 Main Street"
              />
            </div>
            <div>
              <FormLabel htmlFor="address_line2">Address Line 2</FormLabel>
              <Input
                id="address_line2"
                type="text"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                className="w-full h-8"
                placeholder="Apartment, suite, etc. (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FormLabel htmlFor="city">City</FormLabel>
                <Input
                  id="city"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full h-8"
                  placeholder="London"
                />
              </div>
              <div>
                <FormLabel htmlFor="county">County</FormLabel>
                <Input
                  id="county"
                  type="text"
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  className="w-full h-8"
                  placeholder="Greater London"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FormLabel htmlFor="postcode">Postcode</FormLabel>
                <Input
                  id="postcode"
                  type="text"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  className="w-full h-8"
                  placeholder="SW1A 1AA"
                />
              </div>
              <div>
                <FormLabel htmlFor="country">Country</FormLabel>
                <Input
                  id="country"
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  maxLength={2}
                  className="w-full h-8"
                  placeholder="GB"
                />
              </div>
            </div>
          </div>

          {/* Financial */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormLabel htmlFor="credit_limit">Credit Limit (£)</FormLabel>
              <Input
                id="credit_limit"
                type="number"
                name="credit_limit"
                value={formData.credit_limit}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full h-8"
                placeholder="0.00"
              />
            </div>
            <div>
              <FormLabel htmlFor="current_balance">Current Balance (£)</FormLabel>
              <Input
                id="current_balance"
                type="number"
                name="current_balance"
                value={formData.current_balance}
                onChange={handleChange}
                step="0.01"
                className="w-full h-8"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <FormLabel htmlFor="notes">Notes</FormLabel>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              placeholder="Additional notes about this client..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


